import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { format, addWeeks } from "date-fns";
import { 
  ArrowLeft, Ruler, Save, Plus, Check, ChevronDown, Search, X, Send, 
  Printer, Copy, ChevronRight, AlertTriangle, Clock, Package, Truck, 
  CheckCircle2, FileText, MessageSquare, Keyboard, ChevronUp, Eye, UserPlus,
  RefreshCw
} from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import { useFabricCatalog } from "@/contexts/FabricCatalogContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GARMENT_TYPES = [
  { value: "jacket", label: "Jacket Only" },
  { value: "trouser", label: "Trouser Only" },
  { value: "vest", label: "Vest Only" },
  { value: "2piece", label: "2-Piece Suit (Jacket + Trouser)" },
  { value: "3piece", label: "3-Piece Suit (Jacket + Trouser + Vest)" },
];

// Adjustment options for dropdowns
const ADJUSTMENT_OPTIONS = [
  { value: -2, label: "-2" }, { value: -1.75, label: "-1 3/4" }, { value: -1.5, label: "-1 1/2" },
  { value: -1.25, label: "-1 1/4" }, { value: -1, label: "-1" }, { value: -0.875, label: "-7/8" },
  { value: -0.75, label: "-3/4" }, { value: -0.625, label: "-5/8" }, { value: -0.5, label: "-1/2" },
  { value: -0.375, label: "-3/8" }, { value: -0.25, label: "-1/4" }, { value: -0.125, label: "-1/8" },
  { value: 0, label: "0" },
  { value: 0.125, label: "+1/8" }, { value: 0.25, label: "+1/4" }, { value: 0.375, label: "+3/8" },
  { value: 0.5, label: "+1/2" }, { value: 0.625, label: "+5/8" }, { value: 0.75, label: "+3/4" },
  { value: 0.875, label: "+7/8" }, { value: 1, label: "+1" }, { value: 1.25, label: "+1 1/4" },
  { value: 1.5, label: "+1 1/2" }, { value: 1.75, label: "+1 3/4" }, { value: 2, label: "+2" },
];

const POSTURE_OPTIONS = [
  { value: -2, label: "-2" }, { value: -1.5, label: "-1 1/2" }, { value: -1, label: "-1" },
  { value: -0.75, label: "-3/4" }, { value: -0.5, label: "-1/2" }, { value: -0.25, label: "-1/4" },
  { value: 0, label: "0" },
  { value: 0.25, label: "+1/4" }, { value: 0.5, label: "+1/2" }, { value: 0.75, label: "+3/4" },
  { value: 1, label: "+1" }, { value: 1.5, label: "+1 1/2" }, { value: 2, label: "+2" },
];

// Jacket Styling Options
const JACKET_BODY_STYLES = ["Slim", "Regular", "Classic", "Relaxed"];
const JACKET_FRONT_CLOSURES = [
  "1-Button", "2-Button", "3-Button", "3-Roll-2", "4-Button",
  "2x1 DB", "4x2 DB", "6x1 DB", "6x2 DB", "8x2 DB"
];
const LAPEL_TYPES = ["Notch", "Peak", "Shawl"];
const LAPEL_WIDTHS = ["Slim (2.5\")", "Standard (3\")", "Wide (3.5\")", "Extra Wide (4\")"];
const WAIST_POCKET_TYPES = ["Welt", "Flap", "Patch", "No Pocket"];
const WAIST_POCKET_ANGLES = ["Straight", "Slanted"];
const TICKET_POCKETS = ["None", "Left Side", "Right Side"];
const CHEST_POCKETS = ["None", "Welt", "Patch"];
const BUTTON_STYLES = ["Horn", "Corozo", "Mother of Pearl", "Metal"];
const EXTERIOR_STITCHES = ["None", "1/16\" Pic", "5/16\" Pic", "1/16\" Top", "5/16\" Top", "1/4\" Double"];
const SLEEVE_BUTTONHOLES = ["4 Kissing", "4 Non-Kissing", "3 Kissing", "3 Non-Kissing", "No Buttons"];
const SLEEVE_BUTTON_STYLES = ["Same as Front", "Contrasting"];
const UNDER_COLLAR_FELTS = ["Standard", "Premium Melton"];
const LAPEL_BUTTONHOLES = ["None", "Working Buttonhole", "Milanese Hole", "Keyhole"];
const VENTS = ["Single Vent", "Double Vent", "No Vent"];
const FRENCH_FACINGS = ["None", "Standard", "Extended", "Full"];
const INTERIOR_LABEL_TYPES = ["None", "Standard", "Milanese Single Color ($25)", "Milanese Multi Color ($35)", "Custom Embroidered ($45)"];

// Pants Styling Options
const PANT_BODY_STYLES = ["Slim", "Regular", "Classic", "Relaxed"];
const PANT_FRONT_STYLES = ["Flat Front", "Single Pleat", "Double Pleat"];
const FRONT_POCKETS = ["Slant", "On-Seam", "Western"];
const BACK_POCKET_CLOSURES = ["Button", "No Button"];
const BOTTOM_FINISHINGS = ["Plain Hem", "Cuff 1.5\"", "Cuff 1.75\"", "Cuff 2\""];
const BACK_POCKET_STYLES = ["Double Welt", "Single Welt", "Patch"];
const FRONT_CLOSURES_PANT = ["Button & Tab", "Hook & Bar", "Extended Tab"];
const WAISTBAND_STYLES = ["Standard", "Extended", "Side Tabs"];
const WATCH_POCKETS = ["Yes", "No"];
const SUSPENDER_BUTTONS = ["Yes", "No"];

// Vest Styling Options
const VEST_LAPELS = ["None", "Notch", "Peak", "Shawl"];
const VEST_FRONT_CLOSURES = ["5-Button", "6-Button", "4-Button"];
const VEST_POCKET_TYPES = ["Welt", "Patch", "No Pockets"];
const VEST_POCKET_NUMBERS = ["2 Pockets", "4 Pockets"];
const VEST_BACKS = ["Same Fabric", "Lining", "Belt Back"];

// ============ NEW MTM SYSTEM CONSTANTS ============

// Measurement Method Options
const MEASUREMENT_METHODS = [
  { value: "in_person", label: "In-Person", description: "Measured at store" },
  { value: "video", label: "Video Call", description: "Remote video measurement" },
  { value: "self_measured", label: "Self-Measured", description: "Client provided measurements" },
  { value: "previous_order", label: "Previous Order", description: "Based on prior fitting" }
];

// Fit Preference Options
const FIT_PREFERENCES = {
  chest: [
    { value: "slim", label: "Slim", code: "CS" },
    { value: "regular", label: "Regular", code: "CR" },
    { value: "full", label: "Full", code: "CF" }
  ],
  waistSuppression: [
    { value: "low", label: "Low (Relaxed)", code: "WL" },
    { value: "medium", label: "Medium (Standard)", code: "WM" },
    { value: "aggressive", label: "Aggressive (Shaped)", code: "WA" }
  ],
  shoulder: [
    { value: "natural", label: "Natural", code: "SN" },
    { value: "soft", label: "Soft", code: "SS" },
    { value: "structured", label: "Structured", code: "ST" }
  ],
  trouserBreak: [
    { value: "none", label: "No Break", code: "BN" },
    { value: "quarter", label: "Quarter Break", code: "BQ" },
    { value: "half", label: "Half Break", code: "BH" },
    { value: "full", label: "Full Break", code: "BF" }
  ]
};

// Factory Option Codes - Standardized codes for production
const FACTORY_CODES = {
  lapel: {
    "Notch": "LN", "Peak": "LP", "Shawl": "LS"
  },
  lapelWidth: {
    "Slim (2.5\")": "LW25", "Standard (3\")": "LW30", "Wide (3.5\")": "LW35", "Extra Wide (4\")": "LW40"
  },
  frontClosure: {
    "1-Button": "1B", "2-Button": "2B", "3-Button": "3B", "3-Roll-2": "3R2",
    "4-Button": "4B", "2x1 DB": "DB21", "4x2 DB": "DB42", "6x1 DB": "DB61", "6x2 DB": "DB62", "8x2 DB": "DB82"
  },
  vent: {
    "Single Vent": "VS", "Double Vent": "VD", "No Vent": "VN"
  },
  pocket: {
    "Welt": "PW", "Flap": "PF", "Patch": "PP", "No Pocket": "PN"
  },
  pocketAngle: {
    "Straight": "AS", "Slanted": "AN"
  },
  buttonStyle: {
    "Horn": "BH", "Corozo": "BC", "Mother of Pearl": "BP", "Metal": "BM"
  },
  pantFront: {
    "Flat Front": "FF", "Single Pleat": "SP", "Double Pleat": "DP"
  },
  pantBottom: {
    "Plain Hem": "PH", "Cuff 1.5\"": "C15", "Cuff 1.75\"": "C175", "Cuff 2\"": "C20"
  }
};

// RCMTM Piping Catalog
const PIPING_CATALOG = [
  { code: "PIP-BLK", name: "Black Satin", color: "Black", material: "Satin", price: 35 },
  { code: "PIP-NVY", name: "Navy Satin", color: "Navy", material: "Satin", price: 35 },
  { code: "PIP-BRG", name: "Burgundy Satin", color: "Burgundy", material: "Satin", price: 35 },
  { code: "PIP-GRY", name: "Charcoal Satin", color: "Charcoal", material: "Satin", price: 35 },
  { code: "PIP-WHT", name: "White Satin", color: "White", material: "Satin", price: 35 },
  { code: "PIP-CRM", name: "Cream Satin", color: "Cream", material: "Satin", price: 35 },
  { code: "PIP-GLD", name: "Gold Satin", color: "Gold", material: "Satin", price: 45 },
  { code: "PIP-SLV", name: "Silver Satin", color: "Silver", material: "Satin", price: 45 },
  { code: "PIP-RED", name: "Red Satin", color: "Red", material: "Satin", price: 35 },
  { code: "PIP-GRN", name: "Hunter Green Satin", color: "Hunter Green", material: "Satin", price: 35 },
  { code: "PIP-BLK-GRS", name: "Black Grosgrain", color: "Black", material: "Grosgrain", price: 40 },
  { code: "PIP-NVY-GRS", name: "Navy Grosgrain", color: "Navy", material: "Grosgrain", price: 40 },
  { code: "PIP-BRG-GRS", name: "Burgundy Grosgrain", color: "Burgundy", material: "Grosgrain", price: 40 },
  { code: "PIP-VELVET-BLK", name: "Black Velvet", color: "Black", material: "Velvet", price: 55 },
  { code: "PIP-VELVET-NVY", name: "Navy Velvet", color: "Navy", material: "Velvet", price: 55 },
  { code: "PIP-VELVET-BRG", name: "Burgundy Velvet", color: "Burgundy", material: "Velvet", price: 55 },
  { code: "PIP-CONTRAST", name: "Contrast (Match Fabric)", color: "Contrast", material: "Self Fabric", price: 25 },
  { code: "PIP-CUSTOM", name: "Custom Color", color: "Custom", material: "Various", price: 50 }
];

// Piping Location Options
const PIPING_LOCATIONS = {
  jacket: [
    { value: "lapel", label: "Lapel Edge" },
    { value: "pocket_welt", label: "Pocket Welt" },
    { value: "chest_pocket", label: "Chest Pocket" },
    { value: "cuff", label: "Sleeve Cuff" },
    { value: "collar", label: "Under Collar" },
    { value: "back_seam", label: "Back Seam" }
  ],
  pant: [
    { value: "side_seam", label: "Side Seam (Tuxedo Stripe)" },
    { value: "pocket_welt", label: "Pocket Welt" },
    { value: "waistband", label: "Waistband" }
  ],
  vest: [
    { value: "edge", label: "Front Edge" },
    { value: "pocket", label: "Pocket Welt" },
    { value: "armhole", label: "Armhole" },
    { value: "back", label: "Back Panel" }
  ]
};

// Validation Rules for impossible measurements
const VALIDATION_RULES = {
  jacket: {
    chest: { min: 32, max: 62, warn: "Chest measurement outside typical range (32-62\")" },
    stomach: { min: 28, max: 60, warn: "Stomach measurement outside typical range" },
    shoulder: { min: 14, max: 22, warn: "Shoulder measurement outside typical range (14-22\")" },
    sleeve_length: { min: 22, max: 38, warn: "Sleeve length outside typical range (22-38\")" },
    back_length: { min: 26, max: 36, warn: "Back length outside typical range (26-36\")" }
  },
  pant: {
    waist: { min: 26, max: 54, warn: "Waist measurement outside typical range (26-54\")" },
    seat: { min: 32, max: 58, warn: "Seat measurement outside typical range" },
    thigh: { min: 18, max: 34, warn: "Thigh measurement outside typical range" },
    pant_length: { min: 26, max: 38, warn: "Inseam outside typical range (26-38\")" }
  },
  ratios: [
    { 
      check: (j, p) => j.chest && p.waist && (j.chest - p.waist) < 4,
      warn: "Chest-to-waist ratio seems unusual (typically 4\"+ difference)"
    },
    {
      check: (j, p) => j.chest && p.waist && (j.chest - p.waist) > 14,
      warn: "Extreme chest-to-waist difference (>14\") - please verify"
    }
  ]
};

const AdminMTMOrderPage = () => {
  const [clients, setClients] = useState([]);
  const [sizeCharts, setSizeCharts] = useState({ jacket: [], pant: [], vest: [] });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [clientOpen, setClientOpen] = useState(false);
  const [newClientDialog, setNewClientDialog] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", email: "", phone: "" });
  const [creatingCRMProfile, setCreatingCRMProfile] = useState(false);
  
  // Measurement selection dialog
  const [measurementSelectOpen, setMeasurementSelectOpen] = useState(false);
  const [pendingClient, setPendingClient] = useState(null);
  const [selectedMeasurements, setSelectedMeasurements] = useState({ jacket: true, trouser: true, vest: true });
  
  // Client Order History
  const [clientOrders, setClientOrders] = useState([]);
  const [loadingClientOrders, setLoadingClientOrders] = useState(false);
  const [showCopyOrderDialog, setShowCopyOrderDialog] = useState(false);
  const [selectedOrderToCopy, setSelectedOrderToCopy] = useState(null);
  const [copyOptions, setCopyOptions] = useState({
    measurements: true,
    fabric: true,
    buttons: true,
    styles: true
  });
  
  // New Order Dialog state
  const [showNewOrderDialog, setShowNewOrderDialog] = useState(false);
  const [savingBeforeNew, setSavingBeforeNew] = useState(false);
  
  // Load Order Dialog state
  const [showLoadOrderDialog, setShowLoadOrderDialog] = useState(false);
  const [draftOrders, setDraftOrders] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  
  // Form state
  const [selectedClient, setSelectedClient] = useState(null);
  const [orderNumber, setOrderNumber] = useState("");
  
  // Independent garment selection (checkboxes instead of dropdown)
  const [includeJacket, setIncludeJacket] = useState(false);
  const [includeTrouser, setIncludeTrouser] = useState(false);
  const [includeVest, setIncludeVest] = useState(false);
  const [includeShirt, setIncludeShirt] = useState(false);
  const [fabricSelection, setFabricSelection] = useState(null);
  const [liningSelection, setLiningSelection] = useState(null);
  const [constructionType, setConstructionType] = useState("Half Canvas");
  
  // General Info state
  const [generalInfo, setGeneralInfo] = useState({
    store: "Laguerre",
    salesperson: "",
    orderDate: new Date().toISOString().split('T')[0],
    dateWanted: format(addWeeks(new Date(), 4), "yyyy-MM-dd"),
    garmentType: "",
    reference: "",
    customerFirstName: "",
    customerLastName: "",
    email: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    phoneDay: "",
    phoneEve: "",
    cellPhone: ""
  });
  
  // ============ NEW MTM SYSTEM STATE ============
  
  // Measurement Method & Fitter
  const [measurementMethod, setMeasurementMethod] = useState("in_person");
  const [fitter, setFitter] = useState("");
  const [measurementProfileName, setMeasurementProfileName] = useState("");
  
  // Fit Preferences
  const [fitPreferences, setFitPreferences] = useState({
    chest: "regular",
    waistSuppression: "medium",
    shoulder: "natural",
    trouserBreak: "quarter"
  });
  
  // Body Posture Notes (structured)
  const [bodyPostureNotes, setBodyPostureNotes] = useState({
    shoulderSlope: "even", // even, left_high, right_high
    stance: "neutral", // neutral, forward, back
    seatProminence: "normal" // normal, prominent, flat
  });
  
  // Enhanced Validation Warnings
  const [measurementWarnings, setMeasurementWarnings] = useState([]);
  
  // Change Tracking
  const [changeLog, setChangeLog] = useState([]);
  
  // Fabric/Lining dialog state
  const [showFabricDialog, setShowFabricDialog] = useState(false);
  const [showLiningDialog, setShowLiningDialog] = useState(false);
  const [fabricSearch, setFabricSearch] = useState("");
  const [liningSearch, setLiningSearch] = useState("");
  const [fabricCategory, setFabricCategory] = useState("suits");
  const [fabricFilters, setFabricFilters] = useState({ composition: "all", pattern: "all", color: "all" });
  
  // Jacket Measurements State
  const [jacketModelSize, setJacketModelSize] = useState("");
  const [jacketStartingSpecs, setJacketStartingSpecs] = useState({});
  const [jacketAdjustments, setJacketAdjustments] = useState({
    chest: 0, stomach: 0, seat: 0, shoulder: 0, bicep: 0,
    sleeve_length: 0, back_length: 0, front_length: 0, cuff: 0
  });
  const [jacketPosture, setJacketPosture] = useState({
    stoop: 0, erect: 0, high_shoulder: 0, sloping_shoulder: 0, forward_shoulder: 0
  });
  const [jacketRemarks, setJacketRemarks] = useState("");
  
  // Jacket Styling State
  const [jacketStyling, setJacketStyling] = useState({
    body_style: "Regular", front_closure: "2-Button", lapel_type: "Notch",
    lapel_width: "3\" (7.6cm)", waist_pocket_type: "Flap", waist_pocket_angle: "Straight",
    ticket_pocket: "None", chest_pocket: "Welt", button_style: "Horn",
    exterior_stitch: "None", sleeve_buttonholes: "4 Kissing",
    sleeve_button_style: "Same as Front", under_collar_felt: "Standard", lapel_buttonhole: "None",
    vent: "Double Vent", french_facing: "None", under_collar_monogram: "",
    piping: { enabled: false, location: [], color: "", catalog_code: "", custom_details: "" }
  });
  const [jacketStylingRemarks, setJacketStylingRemarks] = useState("");

  // Pant Measurements State
  const [pantModelSize, setPantModelSize] = useState("");
  const [pantModelType, setPantModelType] = useState("standard");
  const [pantFrontStyle, setPantFrontStyle] = useState("flat_front");
  const [pantStartingSpecs, setPantStartingSpecs] = useState({});
  const [pantAdjustments, setPantAdjustments] = useState({
    waist: 0, seat: 0, urise: 0, thigh: 0, pant_length: 0, knee: 0, bottom: 0
  });
  const [pantPosture, setPantPosture] = useState({
    front_rise: 0, back_rise: 0, side_rise: 0, lift_back_thigh: 0, calf: 0, mid_thigh: 0
  });
  const [pantRemarks, setPantRemarks] = useState("");
  
  // Pant Styling State
  const [pantStyling, setPantStyling] = useState({
    body_style: "Regular", front_style: "Flat Front", front_pockets: "Slant",
    back_pocket_closure: "Button", bottom_finishing: "Plain Hem", back_pocket_style: "Double Welt",
    front_closure: "Button & Tab", waistband_style: "Standard", watch_pocket: "No",
    suspender_buttons: "No", is_tuxedo: false,
    piping: { enabled: false, location: [], color: "", catalog_code: "", custom_details: "" }
  });
  const [pantStylingRemarks, setPantStylingRemarks] = useState("");

  // Vest Measurements State
  const [vestModelSize, setVestModelSize] = useState("");
  const [vestFrontClosure, setVestFrontClosure] = useState("5_button");
  const [vestStartingSpecs, setVestStartingSpecs] = useState({});
  const [vestAdjustments, setVestAdjustments] = useState({
    chest: 0, stomach: 0, back_length: 0, front_length: 0, bottom: 0
  });
  const [vestPosture, setVestPosture] = useState({
    stoop: 0, erect: 0, high_shoulder: 0, sloping_shoulder: 0
  });
  const [vestRemarks, setVestRemarks] = useState("");
  
  // Vest Styling State
  const [vestStyling, setVestStyling] = useState({
    lapel: "None", front_closure: "5-Button", pocket_type: "Welt",
    pocket_number: "4 Pockets", back: "Lining",
    piping: { enabled: false, location: [], color: "", catalog_code: "", custom_details: "" }
  });
  const [vestStylingRemarks, setVestStylingRemarks] = useState("");
  
  // Shirt Measurements State
  const [shirtModelSize, setShirtModelSize] = useState("");
  const [shirtStartingSpecs, setShirtStartingSpecs] = useState({});
  const [shirtAdjustments, setShirtAdjustments] = useState({
    neck: 0, chest: 0, stomach: 0, seat: 0, shoulder: 0,
    right_sleeve_length: 0, left_sleeve_length: 0,
    back_length: 0, right_cuff: 0, left_cuff: 0
  });
  const [shirtRemarks, setShirtRemarks] = useState("");
  
  // Shirt Styling State
  const [shirtStyling, setShirtStyling] = useState({
    collar_style: "Spread", cuff_style: "Button", front_placket: "Plain",
    back_style: "Plain", pocket: "No Pocket", monogram: "", monogram_position: "Cuff"
  });
  const [shirtStylingRemarks, setShirtStylingRemarks] = useState("");
  
  // Piping Catalog Selection
  const [showPipingCatalog, setShowPipingCatalog] = useState(false);
  const [activePipingGarment, setActivePipingGarment] = useState(null); // 'jacket', 'pant', 'vest'

  // ============ NEW ENHANCED FEATURES ============
  
  // Factory Notes (internal only)
  const [factoryNotes, setFactoryNotes] = useState("");
  const [rushOrder, setRushOrder] = useState(false);
  const [priority, setPriority] = useState("normal"); // normal, high, urgent
  
  // Order Status Workflow
  const [orderStatus, setOrderStatus] = useState("draft");
  const ORDER_STATUSES = [
    { id: "draft", label: "Draft", icon: FileText, color: "bg-gray-500" },
    { id: "submitted", label: "Submitted", icon: Send, color: "bg-blue-500" },
    { id: "in_production", label: "In Production", icon: Package, color: "bg-yellow-500" },
    { id: "shipped", label: "Shipped", icon: Truck, color: "bg-purple-500" },
    { id: "complete", label: "Complete", icon: CheckCircle2, color: "bg-green-500" }
  ];
  
  // Collapsible Sections
  const [collapsedSections, setCollapsedSections] = useState({
    general: false,
    fabric: false,
    jacket: false,
    trouser: false,
    vest: false,
    submit: false
  });
  
  // Auto-save
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const autoSaveTimerRef = useRef(null);
  
  // Print dialog
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  
  // Duplicate order
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [selectedOrderToDuplicate, setSelectedOrderToDuplicate] = useState(null);
  
  // Keyboard shortcuts help
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  
  // Validation warnings
  const [validationWarnings, setValidationWarnings] = useState([]);
  
  // Active section for progress tracking
  const [activeSection, setActiveSection] = useState("general");

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { FABRIC_DATA, MX_FABRIC_DATA } = useFabricCatalog();
  
  // Toggle section collapse
  const toggleSection = (section) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Calculate section completion percentages
  const sectionProgress = useMemo(() => {
    const generalComplete = [
      generalInfo.customerFirstName,
      generalInfo.customerLastName,
      selectedClient,
      (includeJacket || includeTrouser || includeVest || includeShirt)
    ].filter(Boolean).length / 4 * 100;
    
    const fabricComplete = [fabricSelection, liningSelection].filter(Boolean).length / 2 * 100;
    
    let measurementsComplete = 0;
    let measurementItems = 0;
    
    if (includeJacket) {
      measurementItems += 2;
      if (jacketModelSize) measurementsComplete++;
      if (Object.keys(jacketStartingSpecs).length > 0) measurementsComplete++;
    }
    if (includeTrouser) {
      measurementItems += 2;
      if (pantModelSize) measurementsComplete++;
      if (Object.keys(pantStartingSpecs).length > 0) measurementsComplete++;
    }
    if (includeVest) {
      measurementItems += 2;
      if (vestModelSize) measurementsComplete++;
      if (Object.keys(vestStartingSpecs).length > 0) measurementsComplete++;
    }
    
    const measurementsPercent = measurementItems > 0 ? (measurementsComplete / measurementItems) * 100 : 0;
    
    return {
      general: Math.round(generalComplete),
      fabric: Math.round(fabricComplete),
      measurements: Math.round(measurementsPercent),
      overall: Math.round((generalComplete + fabricComplete + measurementsPercent) / 3)
    };
  }, [generalInfo, selectedClient, includeJacket, includeTrouser, includeVest, fabricSelection, liningSelection, jacketModelSize, jacketStartingSpecs, pantModelSize, pantStartingSpecs, vestModelSize, vestStartingSpecs]);

  // Real-time validation with enhanced rules
  useEffect(() => {
    const warnings = [];
    
    // Jacket validations
    if (includeJacket && Object.keys(jacketStartingSpecs).length > 0) {
      const finalChest = (jacketStartingSpecs.chest || 0) + (jacketAdjustments.chest || 0);
      const finalStomach = (jacketStartingSpecs.stomach || 0) + (jacketAdjustments.stomach || 0);
      const finalShoulder = (jacketStartingSpecs.shoulder || 0) + (jacketAdjustments.shoulder || 0);
      const finalSleeve = (jacketStartingSpecs.sleeve_length || 0) + (jacketAdjustments.sleeve_length || 0);
      const finalBack = (jacketStartingSpecs.back_length || 0) + (jacketAdjustments.back_length || 0);
      
      if (finalChest && (finalChest < 32 || finalChest > 62)) {
        warnings.push({ section: "jacket", field: "chest", severity: "error", message: `Chest ${finalChest}" outside range (32-62")` });
      }
      if (finalShoulder && (finalShoulder < 14 || finalShoulder > 22)) {
        warnings.push({ section: "jacket", field: "shoulder", severity: "warning", message: `Shoulder ${finalShoulder}" unusual (14-22")` });
      }
      if (finalSleeve && (finalSleeve < 22 || finalSleeve > 38)) {
        warnings.push({ section: "jacket", field: "sleeve", severity: "warning", message: `Sleeve ${finalSleeve}" unusual (22-38")` });
      }
      if (finalBack && (finalBack < 26 || finalBack > 36)) {
        warnings.push({ section: "jacket", field: "back", severity: "warning", message: `Back length ${finalBack}" unusual (26-36")` });
      }
    }
    
    // Trouser validations
    if (includeTrouser && Object.keys(pantStartingSpecs).length > 0) {
      const finalWaist = (pantStartingSpecs.waist || 0) + (pantAdjustments.waist || 0);
      const finalSeat = (pantStartingSpecs.seat || 0) + (pantAdjustments.seat || 0);
      const finalThigh = (pantStartingSpecs.thigh || 0) + (pantAdjustments.thigh || 0);
      const finalInseam = (pantStartingSpecs.pant_length || 0) + (pantAdjustments.pant_length || 0);
      
      if (finalWaist && (finalWaist < 26 || finalWaist > 54)) {
        warnings.push({ section: "trouser", field: "waist", severity: "error", message: `Waist ${finalWaist}" outside range (26-54")` });
      }
      if (finalThigh && (finalThigh < 18 || finalThigh > 34)) {
        warnings.push({ section: "trouser", field: "thigh", severity: "warning", message: `Thigh ${finalThigh}" unusual (18-34")` });
      }
      if (finalInseam && (finalInseam < 26 || finalInseam > 38)) {
        warnings.push({ section: "trouser", field: "inseam", severity: "warning", message: `Inseam ${finalInseam}" unusual (26-38")` });
      }
    }
    
    // Cross-garment ratio checks
    if (includeJacket && includeTrouser && jacketStartingSpecs.chest && pantStartingSpecs.waist) {
      const chestFinal = (jacketStartingSpecs.chest || 0) + (jacketAdjustments.chest || 0);
      const waistFinal = (pantStartingSpecs.waist || 0) + (pantAdjustments.waist || 0);
      const dropDiff = chestFinal - waistFinal;
      
      if (dropDiff < 4) {
        warnings.push({ section: "ratio", field: "drop", severity: "warning", message: `Chest-waist drop ${dropDiff}" is low (typical: 4-10")` });
      }
      if (dropDiff > 14) {
        warnings.push({ section: "ratio", field: "drop", severity: "error", message: `Extreme chest-waist drop ${dropDiff}" - verify measurements` });
      }
    }
    
    setValidationWarnings(warnings);
    setMeasurementWarnings(warnings);
  }, [includeJacket, includeTrouser, jacketStartingSpecs, jacketAdjustments, pantStartingSpecs, pantAdjustments]);

  // Generate factory codes for order
  const generateFactoryCodes = useCallback(() => {
    const codes = [];
    
    // Jacket styling codes
    if (includeJacket) {
      codes.push(`LAP=${FACTORY_CODES.lapel[jacketStyling.lapel_type] || 'LN'}`);
      codes.push(`BTN=${FACTORY_CODES.frontClosure[jacketStyling.front_closure] || '2B'}`);
      codes.push(`VNT=${FACTORY_CODES.vent[jacketStyling.vent] || 'VD'}`);
      codes.push(`PKT=${FACTORY_CODES.pocket[jacketStyling.waist_pocket_type] || 'PF'}`);
    }
    
    // Pant styling codes
    if (includeTrouser) {
      codes.push(`PNT=${FACTORY_CODES.pantFront[pantStyling.front_style] || 'FF'}`);
      codes.push(`BTM=${FACTORY_CODES.pantBottom[pantStyling.bottom_finishing] || 'PH'}`);
    }
    
    // Fit preference codes
    const fitCodes = [];
    fitCodes.push(FIT_PREFERENCES.chest.find(f => f.value === fitPreferences.chest)?.code || 'CR');
    fitCodes.push(FIT_PREFERENCES.waistSuppression.find(f => f.value === fitPreferences.waistSuppression)?.code || 'WM');
    fitCodes.push(FIT_PREFERENCES.shoulder.find(f => f.value === fitPreferences.shoulder)?.code || 'SN');
    fitCodes.push(FIT_PREFERENCES.trouserBreak.find(f => f.value === fitPreferences.trouserBreak)?.code || 'BQ');
    codes.push(`FIT=${fitCodes.join('-')}`);
    
    return codes;
  }, [includeJacket, includeTrouser, jacketStyling, pantStyling, fitPreferences]);

  // Auto-save to localStorage every 30 seconds
  useEffect(() => {
    if (hasUnsavedChanges) {
      autoSaveTimerRef.current = setTimeout(() => {
        const draftData = {
          generalInfo,
          selectedClient,
          includeJacket, includeTrouser, includeVest,
          fabricSelection, liningSelection,
          jacketModelSize, jacketStartingSpecs, jacketAdjustments, jacketPosture, jacketStyling, jacketRemarks,
          pantModelSize, pantStartingSpecs, pantAdjustments, pantPosture, pantStyling, pantRemarks,
          vestModelSize, vestStartingSpecs, vestAdjustments, vestPosture, vestStyling, vestRemarks,
          factoryNotes, rushOrder, priority,
          savedAt: new Date().toISOString()
        };
        localStorage.setItem("mtm_draft", JSON.stringify(draftData));
        setLastAutoSave(new Date());
        setHasUnsavedChanges(false);
        toast.success("Draft auto-saved", { duration: 2000 });
      }, 30000);
    }
    
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [hasUnsavedChanges, generalInfo, selectedClient, includeJacket, includeTrouser, includeVest, fabricSelection, liningSelection, jacketModelSize, jacketStartingSpecs, jacketAdjustments, jacketPosture, jacketStyling, jacketRemarks, pantModelSize, pantStartingSpecs, pantAdjustments, pantPosture, pantStyling, pantRemarks, vestModelSize, vestStartingSpecs, vestAdjustments, vestPosture, vestStyling, vestRemarks, factoryNotes, rushOrder, priority]);

  // Mark as having unsaved changes
  useEffect(() => {
    setHasUnsavedChanges(true);
  }, [generalInfo, includeJacket, includeTrouser, includeVest, fabricSelection, liningSelection, jacketAdjustments, pantAdjustments, vestAdjustments, factoryNotes]);

  // Auto-set garments based on Garment Type selection
  useEffect(() => {
    const garmentType = generalInfo.garmentType;
    if (!garmentType) return;
    
    switch (garmentType) {
      case "2-Piece Suit":
        setIncludeJacket(true);
        setIncludeTrouser(true);
        setIncludeVest(false);
        setIncludeShirt(false);
        break;
      case "3-Piece Suit":
        setIncludeJacket(true);
        setIncludeTrouser(true);
        setIncludeVest(true);
        setIncludeShirt(false);
        break;
      case "Sports Coat":
      case "Blazer":
      case "Jacket":
        setIncludeJacket(true);
        setIncludeTrouser(false);
        setIncludeVest(false);
        setIncludeShirt(false);
        break;
      case "Trousers":
        setIncludeJacket(false);
        setIncludeTrouser(true);
        setIncludeVest(false);
        setIncludeShirt(false);
        break;
      case "Vest":
        setIncludeJacket(false);
        setIncludeTrouser(false);
        setIncludeVest(true);
        setIncludeShirt(false);
        break;
      case "Shirt":
        setIncludeJacket(false);
        setIncludeTrouser(false);
        setIncludeVest(false);
        setIncludeShirt(true);
        break;
      case "Tuxedo":
        setIncludeJacket(true);
        setIncludeTrouser(true);
        setIncludeVest(false);
        setIncludeShirt(false);
        break;
      default:
        break;
    }
  }, [generalInfo.garmentType]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + S = Save draft
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSaveOrder(false);
        toast.success("Order saved as draft");
      }
      // Ctrl/Cmd + P = Print
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        setShowPrintDialog(true);
      }
      // Ctrl/Cmd + D = Duplicate
      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        e.preventDefault();
        setShowDuplicateDialog(true);
      }
      // ? = Show shortcuts
      if (e.key === '?' && !e.target.matches('input, textarea')) {
        setShowShortcutsHelp(true);
      }
      // Escape = Close dialogs
      if (e.key === 'Escape') {
        setShowPrintDialog(false);
        setShowDuplicateDialog(false);
        setShowShortcutsHelp(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Load draft from localStorage on mount
  useEffect(() => {
    const savedDraft = localStorage.getItem("mtm_draft");
    if (savedDraft && !searchParams.get("order")) {
      const draft = JSON.parse(savedDraft);
      // Only load if draft is less than 24 hours old
      if (draft.savedAt && (new Date() - new Date(draft.savedAt)) < 86400000) {
        toast.info("Restored auto-saved draft", { 
          action: {
            label: "Discard",
            onClick: () => {
              localStorage.removeItem("mtm_draft");
              window.location.reload();
            }
          }
        });
        if (draft.generalInfo) setGeneralInfo(draft.generalInfo);
        if (draft.factoryNotes) setFactoryNotes(draft.factoryNotes);
        if (draft.rushOrder) setRushOrder(draft.rushOrder);
        if (draft.priority) setPriority(draft.priority);
      }
    }
  }, [searchParams]);

  // Duplicate order function
  const handleDuplicateOrder = (order) => {
    if (!order) return;
    
    // Copy relevant fields
    if (order.jacket_measurements) {
      setIncludeJacket(true);
      if (order.jacket_measurements.tryon_size) setJacketModelSize(order.jacket_measurements.tryon_size);
      if (order.jacket_measurements.starting_specs) setJacketStartingSpecs(order.jacket_measurements.starting_specs);
      if (order.jacket_measurements.adjustments) setJacketAdjustments(order.jacket_measurements.adjustments);
      if (order.jacket_measurements.posture) setJacketPosture(order.jacket_measurements.posture);
      if (order.jacket_measurements.styling) setJacketStyling(prev => ({ ...prev, ...order.jacket_measurements.styling }));
    }
    if (order.pant_measurements) {
      setIncludeTrouser(true);
      if (order.pant_measurements.tryon_size) setPantModelSize(order.pant_measurements.tryon_size);
      if (order.pant_measurements.starting_specs) setPantStartingSpecs(order.pant_measurements.starting_specs);
      if (order.pant_measurements.adjustments) setPantAdjustments(order.pant_measurements.adjustments);
      if (order.pant_measurements.posture) setPantPosture(order.pant_measurements.posture);
      if (order.pant_measurements.styling) setPantStyling(prev => ({ ...prev, ...order.pant_measurements.styling }));
    }
    if (order.vest_measurements) {
      setIncludeVest(true);
      if (order.vest_measurements.tryon_size) setVestModelSize(order.vest_measurements.tryon_size);
      if (order.vest_measurements.starting_specs) setVestStartingSpecs(order.vest_measurements.starting_specs);
      if (order.vest_measurements.adjustments) setVestAdjustments(order.vest_measurements.adjustments);
      if (order.vest_measurements.posture) setVestPosture(order.vest_measurements.posture);
      if (order.vest_measurements.styling) setVestStyling(prev => ({ ...prev, ...order.vest_measurements.styling }));
    }
    if (order.fabric_selection) setFabricSelection(order.fabric_selection);
    if (order.lining_selection) setLiningSelection(order.lining_selection);
    
    setShowDuplicateDialog(false);
    toast.success(`Duplicated from order ${order.order_number}`);
  };

  // Print order sheet
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const garmentLabel = GARMENT_TYPES.find(t => t.value === garmentType)?.label || garmentType;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>MTM Order - ${orderNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
          .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 20px; }
          .header h1 { font-size: 24px; margin-bottom: 5px; }
          .header h2 { font-size: 14px; font-weight: normal; }
          .order-info { display: flex; justify-content: space-between; margin-bottom: 20px; }
          .order-info div { flex: 1; }
          .section { margin-bottom: 20px; border: 1px solid #ccc; }
          .section-title { background: #333; color: white; padding: 8px; font-weight: bold; }
          .section-content { padding: 10px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
          th { background: #f5f5f5; }
          .measurements-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 5px; }
          .measurement-item { padding: 5px; background: #f9f9f9; }
          .rush { background: #ffebee; border: 2px solid #f44336; }
          .notes { background: #fff3e0; padding: 10px; margin-top: 10px; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>LAGUERRE</h1>
          <h2>Made-to-Measure Order Form</h2>
        </div>
        
        <div class="order-info ${rushOrder ? 'rush' : ''}">
          <div>
            <strong>Order #:</strong> ${orderNumber}<br>
            <strong>Date:</strong> ${generalInfo.orderDate}<br>
            <strong>Salesperson:</strong> ${generalInfo.salesperson || '-'}
          </div>
          <div>
            <strong>Customer:</strong> ${generalInfo.customerFirstName} ${generalInfo.customerLastName}<br>
            <strong>Email:</strong> ${generalInfo.email || '-'}<br>
            <strong>Phone:</strong> ${generalInfo.cellPhone || '-'}
          </div>
          <div>
            <strong>Garment:</strong> ${garmentLabel}<br>
            <strong>Date Wanted:</strong> ${generalInfo.dateWanted || format(addWeeks(new Date(), 4), "MMM d, yyyy")}<br>
            ${rushOrder ? '<strong style="color:red">⚠️ RUSH ORDER</strong>' : ''}
          </div>
        </div>
        
        ${fabricSelection ? `
        <div class="section">
          <div class="section-title">FABRIC & LINING</div>
          <div class="section-content">
            <strong>Fabric:</strong> ${fabricSelection.rc_code} - ${fabricSelection.composition || ''} ${fabricSelection.color || ''}<br>
            <strong>Lining:</strong> ${liningSelection?.rc_code || 'Not selected'}
          </div>
        </div>
        ` : ''}
        
        ${includeJacket ? `
        <div class="section">
          <div class="section-title">JACKET MEASUREMENTS (Size: ${jacketModelSize || '-'})</div>
          <div class="section-content">
            <table>
              <tr><th>Measurement</th><th>Starting</th><th>Adjust</th><th>Final</th></tr>
              <tr><td>Chest</td><td>${jacketStartingSpecs.chest || '-'}</td><td>${jacketAdjustments.chest || 0}</td><td>${(jacketStartingSpecs.chest || 0) + (jacketAdjustments.chest || 0)}</td></tr>
              <tr><td>Stomach</td><td>${jacketStartingSpecs.stomach || '-'}</td><td>${jacketAdjustments.stomach || 0}</td><td>${(jacketStartingSpecs.stomach || 0) + (jacketAdjustments.stomach || 0)}</td></tr>
              <tr><td>Shoulder</td><td>${jacketStartingSpecs.shoulder || '-'}</td><td>${jacketAdjustments.shoulder || 0}</td><td>${(jacketStartingSpecs.shoulder || 0) + (jacketAdjustments.shoulder || 0)}</td></tr>
              <tr><td>Sleeve Length</td><td>${jacketStartingSpecs.sleeve_length || '-'}</td><td>${jacketAdjustments.sleeve_length || 0}</td><td>${(jacketStartingSpecs.sleeve_length || 0) + (jacketAdjustments.sleeve_length || 0)}</td></tr>
              <tr><td>Back Length</td><td>${jacketStartingSpecs.back_length || '-'}</td><td>${jacketAdjustments.back_length || 0}</td><td>${(jacketStartingSpecs.back_length || 0) + (jacketAdjustments.back_length || 0)}</td></tr>
            </table>
            <p style="margin-top:10px"><strong>Style:</strong> ${jacketStyling.front_closure}, ${jacketStyling.lapel_type} Lapel, ${jacketStyling.vent}</p>
            ${jacketRemarks ? `<p><strong>Notes:</strong> ${jacketRemarks}</p>` : ''}
          </div>
        </div>
        ` : ''}
        
        ${includeTrouser ? `
        <div class="section">
          <div class="section-title">TROUSER MEASUREMENTS (Size: ${pantModelSize || '-'})</div>
          <div class="section-content">
            <table>
              <tr><th>Measurement</th><th>Starting</th><th>Adjust</th><th>Final</th></tr>
              <tr><td>Waist</td><td>${pantStartingSpecs.waist || '-'}</td><td>${pantAdjustments.waist || 0}</td><td>${(pantStartingSpecs.waist || 0) + (pantAdjustments.waist || 0)}</td></tr>
              <tr><td>Seat</td><td>${pantStartingSpecs.seat || '-'}</td><td>${pantAdjustments.seat || 0}</td><td>${(pantStartingSpecs.seat || 0) + (pantAdjustments.seat || 0)}</td></tr>
              <tr><td>Thigh</td><td>${pantStartingSpecs.thigh || '-'}</td><td>${pantAdjustments.thigh || 0}</td><td>${(pantStartingSpecs.thigh || 0) + (pantAdjustments.thigh || 0)}</td></tr>
              <tr><td>Inseam</td><td>${pantStartingSpecs.pant_length || '-'}</td><td>${pantAdjustments.pant_length || 0}</td><td>${(pantStartingSpecs.pant_length || 0) + (pantAdjustments.pant_length || 0)}</td></tr>
            </table>
            <p style="margin-top:10px"><strong>Style:</strong> ${pantStyling.front_style}, ${pantStyling.bottom_finishing}</p>
            ${pantRemarks ? `<p><strong>Notes:</strong> ${pantRemarks}</p>` : ''}
          </div>
        </div>
        ` : ''}
        
        ${includeVest ? `
        <div class="section">
          <div class="section-title">VEST MEASUREMENTS (Size: ${vestModelSize || '-'})</div>
          <div class="section-content">
            <table>
              <tr><th>Measurement</th><th>Starting</th><th>Adjust</th><th>Final</th></tr>
              <tr><td>Chest</td><td>${vestStartingSpecs.chest || '-'}</td><td>${vestAdjustments.chest || 0}</td><td>${(vestStartingSpecs.chest || 0) + (vestAdjustments.chest || 0)}</td></tr>
              <tr><td>Length</td><td>${vestStartingSpecs.back_length || '-'}</td><td>${vestAdjustments.back_length || 0}</td><td>${(vestStartingSpecs.back_length || 0) + (vestAdjustments.back_length || 0)}</td></tr>
            </table>
            ${vestRemarks ? `<p><strong>Notes:</strong> ${vestRemarks}</p>` : ''}
          </div>
        </div>
        ` : ''}
        
        ${factoryNotes ? `
        <div class="notes">
          <strong>⚙️ FACTORY NOTES:</strong><br>
          ${factoryNotes}
        </div>
        ` : ''}
        
        <div style="margin-top: 30px; border-top: 1px solid #ccc; padding-top: 10px;">
          <p><strong>Signature:</strong> _______________________  <strong>Date:</strong> _______________</p>
        </div>
      </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.print();
    setShowPrintDialog(false);
  };

  // Filtered fabric list based on search and category
  const filteredFabrics = useMemo(() => {
    let fabrics = [];
    
    if (fabricCategory === "mx") {
      // MX Fabrics
      fabrics = MX_FABRIC_DATA.map(f => ({
        ...f,
        no: f.rc_code,
        source: "MX"
      }));
    } else {
      // RC Fabrics
      fabrics = (FABRIC_DATA[fabricCategory] || []).map(f => ({
        ...f,
        source: "RC"
      }));
    }
    
    if (!fabricSearch) return fabrics.slice(0, 100);
    const search = fabricSearch.toLowerCase();
    return fabrics.filter(f => {
      const searchFields = [
        f.rc_code,
        f.composition,
        f.color,
        f.pattern,
        f.yarn,
        f.book
      ].filter(Boolean).join(" ").toLowerCase();
      return searchFields.includes(search);
    }).slice(0, 100);
  }, [fabricCategory, fabricSearch]);

  // Filtered lining list based on search
  const filteredLinings = useMemo(() => {
    const linings = FABRIC_DATA.linings || [];
    if (!liningSearch) return linings.slice(0, 100);
    const search = liningSearch.toLowerCase();
    return linings.filter(l => {
      const searchFields = [l.rc_code, l.book, l.category].filter(Boolean).join(" ").toLowerCase();
      return searchFields.includes(search);
    }).slice(0, 100);
  }, [liningSearch]);

  // Get unique values for fabric filters
  const fabricFilterOptions = useMemo(() => {
    const fabrics = FABRIC_DATA[fabricCategory] || [];
    return {
      compositions: [...new Set(fabrics.map(f => f.composition).filter(Boolean))],
      patterns: [...new Set(fabrics.map(f => f.pattern).filter(Boolean))],
      colors: [...new Set(fabrics.map(f => f.color).filter(Boolean))]
    };
  }, [fabricCategory]);

  useEffect(() => {
    const adminAuth = sessionStorage.getItem("adminAuth");
    if (adminAuth !== "true") { navigate("/admin"); return; }
    
    // Auto-populate salesperson from logged-in user
    const adminUser = sessionStorage.getItem("adminUser");
    if (adminUser) {
      try {
        const user = JSON.parse(adminUser);
        // Use full_name first, fallback to username
        const salespersonName = user.full_name || user.username || "";
        if (salespersonName) {
          setGeneralInfo(prev => ({ ...prev, salesperson: salespersonName }));
        }
        // Also set fitter to logged-in user's name
        if (salespersonName) {
          setFitter(salespersonName);
        }
      } catch (e) {
        console.error("Failed to parse admin user:", e);
      }
    }
    
    fetchData();
    
    // Load fabric & lining from localStorage
    const savedFabric = localStorage.getItem("mtm_fabric");
    if (savedFabric) {
      const data = JSON.parse(savedFabric);
      if (data.fabricSelection) setFabricSelection(data.fabricSelection);
      if (data.liningSelection) setLiningSelection(data.liningSelection);
      if (data.constructionType) setConstructionType(data.constructionType);
    }
    
    // Load styles from localStorage
    const savedStyles = localStorage.getItem("mtm_styles");
    if (savedStyles) {
      const styles = JSON.parse(savedStyles);
      if (styles.jacket?.styling) {
        setJacketStyling(prev => ({ ...prev, ...styles.jacket.styling }));
        if (styles.jacket.styling_remarks) setJacketStylingRemarks(styles.jacket.styling_remarks);
      }
      if (styles.pant?.styling) {
        setPantStyling(prev => ({ ...prev, ...styles.pant.styling }));
        if (styles.pant.styling_remarks) setPantStylingRemarks(styles.pant.styling_remarks);
      }
      if (styles.vest?.styling) {
        setVestStyling(prev => ({ ...prev, ...styles.vest.styling }));
        if (styles.vest.styling_remarks) setVestStylingRemarks(styles.vest.styling_remarks);
      }
    }
    
    // Load garment selection from localStorage
    const savedGarments = localStorage.getItem("mtm_garments");
    if (savedGarments) {
      const garments = JSON.parse(savedGarments);
      if (garments.includeJacket !== undefined) setIncludeJacket(garments.includeJacket);
      if (garments.includeTrouser !== undefined) setIncludeTrouser(garments.includeTrouser);
      if (garments.includeVest !== undefined) setIncludeVest(garments.includeVest);
    }
  }, [navigate]);

  // Save garment selection to localStorage when changed
  useEffect(() => {
    localStorage.setItem("mtm_garments", JSON.stringify({
      includeJacket,
      includeTrouser,
      includeVest
    }));
  }, [includeJacket, includeTrouser, includeVest]);

  const fetchData = async () => {
    try {
      const [clientsRes, sizeChartsRes, ordersRes, nextNumRes] = await Promise.all([
        axios.get(`${API}/crm/clients`),
        axios.get(`${API}/size-chart/all`),
        axios.get(`${API}/mtm-orders`),
        axios.get(`${API}/mtm-orders/next-number`)
      ]);
      setClients(clientsRes.data);
      setSizeCharts(sizeChartsRes.data);
      setOrders(ordersRes.data);
      setOrderNumber(nextNumRes.data.next_number);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch client's previous orders
  const fetchClientOrders = async (clientId) => {
    if (!clientId) {
      setClientOrders([]);
      return;
    }
    setLoadingClientOrders(true);
    try {
      const response = await axios.get(`${API}/mtm-orders/client/${clientId}`);
      setClientOrders(response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      console.error("Error fetching client orders:", error);
      setClientOrders([]);
    } finally {
      setLoadingClientOrders(false);
    }
  };

  // Copy order data from previous order
  const copyFromOrder = async (order) => {
    if (!order) return;
    
    try {
      // Get full order details
      const fullOrder = order.id ? await axios.get(`${API}/mtm-orders/${order.id}`) : { data: order };
      const orderData = fullOrder.data || order;
      
      // Copy measurements if selected
      if (copyOptions.measurements) {
        if (orderData.jacket_measurements) {
          setJacketStartingSpecs(orderData.jacket_measurements);
          setIncludeJacket(true);
        }
        if (orderData.pant_measurements) {
          setPantStartingSpecs(orderData.pant_measurements);
          setIncludeTrouser(true);
        }
        if (orderData.vest_measurements) {
          setVestStartingSpecs(orderData.vest_measurements);
          setIncludeVest(true);
        }
      }
      
      // Copy fabric if selected
      if (copyOptions.fabric) {
        if (orderData.fabric_selection) setFabricSelection(orderData.fabric_selection);
        if (orderData.lining_selection) setLiningSelection(orderData.lining_selection);
        // Save to localStorage for fabric page
        const fabricData = {
          fabricSelection: orderData.fabric_selection,
          liningSelection: orderData.lining_selection,
          liningType: orderData.lining_type || "Full",
          bodyLining: orderData.body_lining || "",
          piping: orderData.piping || "",
          pipingSelection: orderData.piping_selection || null
        };
        localStorage.setItem("mtm_fabric", JSON.stringify(fabricData));
      }
      
      // Copy buttons if selected
      if (copyOptions.buttons && orderData.button_selection) {
        const fabricData = JSON.parse(localStorage.getItem("mtm_fabric") || "{}");
        fabricData.jacketButtonSelection = orderData.jacket_button_selection || orderData.button_selection;
        fabricData.vestButtonSelection = orderData.vest_button_selection;
        fabricData.shirtButtonSelection = orderData.shirt_button_selection;
        localStorage.setItem("mtm_fabric", JSON.stringify(fabricData));
      }
      
      // Copy styles if selected
      if (copyOptions.styles) {
        if (orderData.jacket_styling) setJacketStyling(orderData.jacket_styling);
        if (orderData.pant_styling) setPantStyling(orderData.pant_styling);
        if (orderData.vest_styling) setVestStyling(orderData.vest_styling);
        // Save to localStorage for styles page
        const stylesData = {
          jacketStyling: orderData.jacket_styling,
          pantStyling: orderData.pant_styling,
          vestStyling: orderData.vest_styling
        };
        localStorage.setItem("mtm_styles", JSON.stringify(stylesData));
      }
      
      // Copy garment type
      if (orderData.garment_type) {
        setGeneralInfo(prev => ({ ...prev, garmentType: orderData.garment_type }));
      }
      
      setShowCopyOrderDialog(false);
      toast.success(`Copied ${[
        copyOptions.measurements && "measurements",
        copyOptions.fabric && "fabric/lining",
        copyOptions.buttons && "buttons",
        copyOptions.styles && "styles"
      ].filter(Boolean).join(", ")} from order ${orderData.order_number}`);
    } catch (error) {
      console.error("Error copying order:", error);
      toast.error("Failed to copy order data");
    }
  };

  // Effect to fetch client orders when client changes
  useEffect(() => {
    if (selectedClient?.id) {
      fetchClientOrders(selectedClient.id);
    } else {
      setClientOrders([]);
    }
  }, [selectedClient?.id]);

  // Apply sizes from chart
  const applyJacketSize = (size) => {
    const sizeData = sizeCharts.jacket.find(s => s.size === size);
    if (sizeData) {
      setJacketStartingSpecs({
        chest: sizeData.chest, stomach: sizeData.stomach, seat: sizeData.seat,
        shoulder: sizeData.shoulder, bicep: sizeData.bicep, sleeve_length: sizeData.sleeve_length,
        back_length: sizeData.back_length, front_length: sizeData.front_length, cuff: sizeData.cuff
      });
      setJacketModelSize(size);
      setJacketAdjustments({ chest: 0, stomach: 0, seat: 0, shoulder: 0, bicep: 0, sleeve_length: 0, back_length: 0, front_length: 0, cuff: 0 });
      toast.success(`Applied jacket size ${size}`);
    }
  };

  const applyPantSize = (size) => {
    const sizeData = sizeCharts.pant.find(s => s.size === size);
    if (sizeData) {
      setPantStartingSpecs({
        waist: sizeData.waist, seat: sizeData.seat, urise: sizeData.urise,
        thigh: sizeData.thigh, pant_length: sizeData.pant_length, knee: sizeData.knee, bottom: sizeData.bottom
      });
      setPantModelSize(size);
      setPantAdjustments({ waist: 0, seat: 0, urise: 0, thigh: 0, pant_length: 0, knee: 0, bottom: 0 });
      toast.success(`Applied pant size ${size}`);
    }
  };

  const applyVestSize = (size) => {
    const sizeData = sizeCharts.vest.find(s => s.size === size);
    if (sizeData) {
      setVestStartingSpecs({
        chest: sizeData.chest, stomach: sizeData.stomach, back_length: sizeData.back_length,
        front_length: sizeData.front_length, bottom: sizeData.bottom
      });
      setVestModelSize(size);
      setVestAdjustments({ chest: 0, stomach: 0, back_length: 0, front_length: 0, bottom: 0 });
      toast.success(`Applied vest size ${size}`);
    }
  };

  // Apply shirt size from chart
  const applyShirtSize = (size) => {
    const sizeData = sizeCharts.shirt?.find(s => s.size === size);
    if (sizeData) {
      setShirtStartingSpecs({
        neck: sizeData.neck || 0, chest: sizeData.chest || 0,
        stomach: sizeData.stomach || 0, seat: sizeData.seat || 0,
        shoulder: sizeData.shoulder || sizeData.shoulders || 0,
        sleeve_length: sizeData.sleeve_length || 0,
        back_length: sizeData.back_length || sizeData.shirt_length || 0,
        cuff: sizeData.cuff || 0
      });
      setShirtModelSize(size);
      setShirtAdjustments({
        neck: 0, chest: 0, stomach: 0, seat: 0, shoulder: 0,
        right_sleeve_length: 0, left_sleeve_length: 0,
        back_length: 0, right_cuff: 0, left_cuff: 0
      });
      toast.success(`Applied shirt size ${size}`);
    }
  };

  // Start a new order - clear all form data and get next order number
  const startNewOrder = async (saveFirst = false) => {
    if (saveFirst) {
      setSavingBeforeNew(true);
      // Save current state to localStorage before clearing
      const currentData = {
        generalInfo,
        jacketStartingSpecs,
        pantStartingSpecs,
        vestStartingSpecs,
        shirtStartingSpecs,
        jacketStyling,
        pantStyling,
        vestStyling,
        includeJacket,
        includeTrouser,
        includeVest,
        includeShirt,
        savedAt: new Date().toISOString()
      };
      localStorage.setItem("mtm_draft_backup", JSON.stringify(currentData));
      toast.success("Current order saved as draft backup");
      setSavingBeforeNew(false);
    }
    
    // Clear all localStorage MTM data
    localStorage.removeItem("mtm_general");
    localStorage.removeItem("mtm_fabric");
    localStorage.removeItem("mtm_measurements");
    localStorage.removeItem("mtm_styles");
    localStorage.removeItem("mtm_garments");
    
    // Reset all form state
    setSelectedClient(null);
    setClientOrders([]);
    setIncludeJacket(false);
    setIncludeTrouser(false);
    setIncludeVest(false);
    setIncludeShirt(false);
    setFabricSelection(null);
    setLiningSelection(null);
    
    // Reset general info with current date
    setGeneralInfo({
      store: "Laguerre",
      salesperson: generalInfo.salesperson, // Keep salesperson
      orderDate: new Date().toISOString().split('T')[0],
      dateWanted: format(addWeeks(new Date(), 4), "yyyy-MM-dd"),
      garmentType: "",
      reference: "",
      customerFirstName: "",
      customerLastName: "",
      email: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      zip: "",
      cellPhone: "",
      measurementMethod: "In-Person Measurement"
    });
    
    // Reset measurements
    setJacketStartingSpecs({});
    setPantStartingSpecs({});
    setVestStartingSpecs({});
    setShirtStartingSpecs({});
    setJacketAdjustments({});
    setPantAdjustments({});
    setVestAdjustments({});
    setShirtAdjustments({});
    setJacketModelSize("");
    setPantModelSize("");
    setVestModelSize("");
    setShirtModelSize("");
    
    // Reset styling
    setJacketStyling({
      body_style: "Regular", front_closure: "2-Button", lapel_type: "Notch",
      lapel_width: "3\" (7.6cm)", waist_pocket_type: "Flap", waist_pocket_angle: "Straight",
      ticket_pocket: "None", chest_pocket: "Welt", button_style: "Horn",
      exterior_stitch: "None", sleeve_buttonholes: "4 Kissing",
      sleeve_button_style: "Same as Front", under_collar_felt: "Standard", lapel_buttonhole: "None",
      vent: "Double Vent", french_facing: "None", under_collar_monogram: "",
      piping: { enabled: false, location: [], color: "", catalog_code: "", custom_details: "" }
    });
    setPantStyling({});
    setVestStyling({});
    setJacketStylingRemarks("");
    setPantStylingRemarks("");
    setVestStylingRemarks("");
    
    // Fetch next order number
    try {
      const response = await axios.get(`${API}/mtm-orders/next-number`);
      setOrderNumber(response.data.next_number);
    } catch (error) {
      console.error("Error fetching next order number:", error);
    }
    
    setShowNewOrderDialog(false);
    toast.success("Form cleared - ready for new order");
  };

  // Fetch draft/pending orders to load
  const fetchDraftOrders = async () => {
    setLoadingDrafts(true);
    try {
      // Get pending/draft orders
      const response = await axios.get(`${API}/mtm-orders`);
      const drafts = response.data.filter(o => 
        o.status === 'pending' || o.status === 'draft'
      ).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setDraftOrders(drafts);
    } catch (error) {
      console.error("Error fetching draft orders:", error);
      toast.error("Failed to load orders");
    } finally {
      setLoadingDrafts(false);
    }
  };

  // Load a saved order into the form
  const loadSavedOrder = async (order) => {
    try {
      // Load client info
      if (order.client_id) {
        const client = clients.find(c => c.id === order.client_id);
        if (client) {
          setSelectedClient(client);
          fetchClientOrders(client.id);
        }
      }
      
      // Set order number
      setOrderNumber(order.order_number);
      
      // Load general info
      setGeneralInfo({
        store: order.store || "Laguerre",
        salesperson: order.salesperson || generalInfo.salesperson,
        orderDate: order.order_date || new Date().toISOString().split('T')[0],
        dateWanted: order.date_wanted || format(addWeeks(new Date(), 4), "yyyy-MM-dd"),
        garmentType: order.garment_type || "",
        reference: order.reference || "",
        customerFirstName: order.client_name?.split(' ')[0] || "",
        customerLastName: order.client_name?.split(' ').slice(1).join(' ') || "",
        email: order.client_email || "",
        address1: order.shipping_address?.address1 || "",
        address2: order.shipping_address?.address2 || "",
        city: order.shipping_address?.city || "",
        state: order.shipping_address?.state || "",
        zip: order.shipping_address?.zip || "",
        cellPhone: order.client_phone || "",
        measurementMethod: order.measurement_method || "In-Person Measurement"
      });
      
      // Set garment inclusions based on garment type
      const gType = order.garment_type?.toLowerCase() || "";
      setIncludeJacket(gType.includes("suit") || gType.includes("jacket") || gType.includes("blazer") || gType.includes("coat"));
      setIncludeTrouser(gType.includes("suit") || gType.includes("trouser") || gType.includes("pant"));
      setIncludeVest(gType.includes("3-piece") || gType.includes("vest"));
      setIncludeShirt(gType.includes("shirt"));
      
      // Load measurements if available
      if (order.jacket_measurements) {
        setJacketStartingSpecs(order.jacket_measurements);
        setJacketModelSize(order.jacket_size || "");
      }
      if (order.pant_measurements) {
        setPantStartingSpecs(order.pant_measurements);
        setPantModelSize(order.pant_size || "");
      }
      if (order.vest_measurements) {
        setVestStartingSpecs(order.vest_measurements);
        setVestModelSize(order.vest_size || "");
      }
      if (order.shirt_measurements) {
        setShirtStartingSpecs(order.shirt_measurements);
        setShirtModelSize(order.shirt_size || "");
      }
      
      // Load fabric selections
      if (order.fabric_selection) setFabricSelection(order.fabric_selection);
      if (order.lining_selection) setLiningSelection(order.lining_selection);
      
      // Save to localStorage for other pages
      const fabricData = {
        fabricSelection: order.fabric_selection,
        liningSelection: order.lining_selection,
        liningType: order.lining_type || "Full",
        bodyLining: order.body_lining || "",
        piping: order.piping || "",
        pipingSelection: order.piping_selection || null,
        jacketButtonSelection: order.jacket_button_selection || order.button_selection,
        vestButtonSelection: order.vest_button_selection,
        shirtButtonSelection: order.shirt_button_selection,
        vestMatchesJacket: order.vest_matches_jacket !== false
      };
      localStorage.setItem("mtm_fabric", JSON.stringify(fabricData));
      
      // Load styling
      if (order.jacket_styling) setJacketStyling(order.jacket_styling);
      if (order.pant_styling) setPantStyling(order.pant_styling);
      if (order.vest_styling) setVestStyling(order.vest_styling);
      
      // Save styles to localStorage
      const stylesData = {
        jacketStyling: order.jacket_styling,
        pantStyling: order.pant_styling,
        vestStyling: order.vest_styling,
        jacketStylingRemarks: order.jacket_styling_remarks || "",
        pantStylingRemarks: order.pant_styling_remarks || "",
        vestStylingRemarks: order.vest_styling_remarks || ""
      };
      localStorage.setItem("mtm_styles", JSON.stringify(stylesData));
      
      // Save garment selections
      localStorage.setItem("mtm_garments", JSON.stringify({
        includeJacket: gType.includes("suit") || gType.includes("jacket"),
        includeTrouser: gType.includes("suit") || gType.includes("trouser"),
        includeVest: gType.includes("3-piece") || gType.includes("vest"),
        includeShirt: gType.includes("shirt"),
        garmentType: order.garment_type
      }));
      
      setShowLoadOrderDialog(false);
      toast.success(`Loaded order ${order.order_number}`);
    } catch (error) {
      console.error("Error loading order:", error);
      toast.error("Failed to load order");
    }
  };

  // Calculate final specs with fractions display
  const calcFinal = (starting, adjustment) => {
    if (!starting && starting !== 0) return "-";
    const final = (starting || 0) + (adjustment || 0);
    const whole = Math.floor(final);
    const frac = final - whole;
    if (frac === 0) return whole.toString();
    if (Math.abs(frac - 0.25) < 0.01) return `${whole} 1/4`;
    if (Math.abs(frac - 0.5) < 0.01) return `${whole} 1/2`;
    if (Math.abs(frac - 0.75) < 0.01) return `${whole} 3/4`;
    if (Math.abs(frac - 0.125) < 0.01) return `${whole} 1/8`;
    if (Math.abs(frac - 0.375) < 0.01) return `${whole} 3/8`;
    if (Math.abs(frac - 0.625) < 0.01) return `${whole} 5/8`;
    if (Math.abs(frac - 0.875) < 0.01) return `${whole} 7/8`;
    return final.toFixed(2);
  };

  // Use checkboxes directly for showing sections
  const showJacket = includeJacket;
  const showPant = includeTrouser;
  const showVest = includeVest;
  const showShirt = includeShirt;
  
  // Compute garment type from checkboxes for saving
  const getGarmentType = () => {
    if (includeJacket && includeTrouser && includeVest) return "3piece";
    if (includeJacket && includeTrouser) return "2piece";
    if (includeJacket) return "jacket";
    if (includeTrouser) return "trouser";
    if (includeVest) return "vest";
    if (includeShirt) return "shirt";
    return "";
  };
  const garmentType = getGarmentType();

  // Create client
  const handleCreateClient = async () => {
    if (!newClient.name) { toast.error("Client name is required"); return; }
    try {
      const response = await axios.post(`${API}/crm/clients`, newClient);
      setClients(prev => [...prev, response.data]);
      setSelectedClient(response.data);
      setNewClientDialog(false);
      setNewClient({ name: "", email: "", phone: "" });
      toast.success("Client created");
    } catch (error) {
      toast.error("Failed to create client");
    }
  };

  // Create CRM Profile from General Info
  const handleCreateCRMProfile = async () => {
    const fullName = `${generalInfo.customerFirstName} ${generalInfo.customerLastName}`.trim();
    if (!fullName) { 
      toast.error("Please enter customer first and last name"); 
      return; 
    }
    
    setCreatingCRMProfile(true);
    try {
      const clientData = {
        name: fullName,
        email: generalInfo.email || "",
        phone: generalInfo.cellPhone || "",
        phone_day: generalInfo.phoneDay || "",
        phone_eve: generalInfo.phoneEve || "",
        address1: generalInfo.address1 || "",
        address2: generalInfo.address2 || "",
        city: generalInfo.city || "",
        state: generalInfo.state || "",
        zip: generalInfo.zip || "",
        address: generalInfo.address1 ? `${generalInfo.address1}${generalInfo.address2 ? ', ' + generalInfo.address2 : ''}, ${generalInfo.city || ''}, ${generalInfo.state || ''} ${generalInfo.zip || ''}`.trim() : "",
        notes: `Created from MTM Order ${orderNumber}`
      };
      
      const response = await axios.post(`${API}/crm/clients`, clientData);
      setClients(prev => [...prev, response.data]);
      setSelectedClient(response.data);
      toast.success(`CRM Profile created for ${fullName}!`);
    } catch (error) {
      console.error("Failed to create CRM profile:", error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Failed to create CRM profile");
      }
    } finally {
      setCreatingCRMProfile(false);
    }
  };

  // Save order
  const handleSaveOrder = async (sendToFactory = false) => {
    if (!selectedClient) { toast.error("Please select a client"); return null; }
    if (!garmentType) { toast.error("Please select a garment type"); return null; }

    setSaving(true);
    try {
      const jacketFinal = showJacket ? {
        tryon_size: jacketModelSize,
        measurements: Object.keys(jacketStartingSpecs).reduce((acc, key) => {
          acc[key] = (jacketStartingSpecs[key] || 0) + (jacketAdjustments[key] || 0);
          return acc;
        }, {}),
        starting_specs: jacketStartingSpecs,
        adjustments: jacketAdjustments,
        posture: jacketPosture,
        styling: jacketStyling,
        remarks: jacketRemarks,
        styling_remarks: jacketStylingRemarks
      } : null;

      const pantFinal = showPant ? {
        tryon_size: pantModelSize,
        model_type: pantModelType,
        measurements: Object.keys(pantStartingSpecs).reduce((acc, key) => {
          acc[key] = (pantStartingSpecs[key] || 0) + (pantAdjustments[key] || 0);
          return acc;
        }, {}),
        starting_specs: pantStartingSpecs,
        adjustments: pantAdjustments,
        posture: pantPosture,
        styling: pantStyling,
        remarks: pantRemarks,
        styling_remarks: pantStylingRemarks
      } : null;

      const vestFinal = showVest ? {
        tryon_size: vestModelSize,
        measurements: Object.keys(vestStartingSpecs).reduce((acc, key) => {
          acc[key] = (vestStartingSpecs[key] || 0) + (vestAdjustments[key] || 0);
          return acc;
        }, {}),
        starting_specs: vestStartingSpecs,
        adjustments: vestAdjustments,
        posture: vestPosture,
        styling: vestStyling,
        remarks: vestRemarks,
        styling_remarks: vestStylingRemarks
      } : null;

      const orderData = {
        order_number: orderNumber,
        client_id: selectedClient.id,
        client_name: selectedClient.name,
        client_email: selectedClient.email || generalInfo.email || "",
        garment_type: garmentType,
        construction_type: constructionType,
        jacket_measurements: jacketFinal,
        pant_measurements: pantFinal,
        vest_measurements: vestFinal,
        fabric_selection: fabricSelection ? {
          rc_code: fabricSelection.rc_code,
          book: fabricSelection.book,
          composition: fabricSelection.composition || "",
          pattern: fabricSelection.pattern || "",
          color: fabricSelection.color || "",
          price: fabricSelection.price || 0,
          category: fabricCategory
        } : null,
        lining_selection: liningSelection ? {
          rc_code: liningSelection.rc_code,
          book: liningSelection.book,
          is_new: liningSelection.is_new || false
        } : null,
        general_info: {
          store: generalInfo.store,
          salesperson: generalInfo.salesperson,
          order_date: generalInfo.orderDate,
          date_wanted: generalInfo.dateWanted || format(addWeeks(new Date(), 4), "yyyy-MM-dd"),
          order_type: generalInfo.garmentType,
          reference: generalInfo.reference,
          customer_first_name: generalInfo.customerFirstName,
          customer_last_name: generalInfo.customerLastName,
          email: generalInfo.email,
          address1: generalInfo.address1,
          address2: generalInfo.address2,
          city: generalInfo.city,
          state: generalInfo.state,
          zip: generalInfo.zip,
          phone_day: generalInfo.phoneDay,
          phone_eve: generalInfo.phoneEve,
          cell_phone: generalInfo.cellPhone
        },
        status: sendToFactory ? "submitted" : "draft",
        order_date: format(new Date(), "yyyy-MM-dd"),
        estimated_arrival: generalInfo.dateWanted || format(addWeeks(new Date(), 4), "yyyy-MM-dd"),
      };

      const response = await axios.post(`${API}/mtm-orders`, orderData);
      
      // Clear localStorage after successful save
      localStorage.removeItem("mtm_fabric");
      localStorage.removeItem("mtm_styles");
      localStorage.removeItem("mtm_garments");
      localStorage.removeItem("mtm_draft");
      
      // Reset garment checkboxes
      setIncludeJacket(false);
      setIncludeTrouser(false);
      setIncludeVest(false);
      setHasUnsavedChanges(false);
      
      // Refresh
      const nextNumRes = await axios.get(`${API}/mtm-orders/next-number`);
      setOrderNumber(nextNumRes.data.next_number);
      const ordersRes = await axios.get(`${API}/mtm-orders`);
      setOrders(ordersRes.data);
      
      return { ...orderData, id: response.data?.id };
    } catch (error) {
      console.error("Failed to save order:", error);
      toast.error("Failed to save order");
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Send order to factory (save + send email confirmation)
  const handleSaveAndSendOrder = async () => {
    setSending(true);
    try {
      const savedOrder = await handleSaveOrder(true);
      if (!savedOrder) {
        setSending(false);
        return;
      }

      // Send confirmation email to client if email exists
      const clientEmail = savedOrder.client_email || generalInfo.email;
      if (clientEmail) {
        try {
          const garmentLabel = GARMENT_TYPES.find(t => t.value === savedOrder.garment_type)?.label || savedOrder.garment_type;
          const customerName = generalInfo.customerFirstName || savedOrder.client_name;
          const fabricInfo = savedOrder.fabric_selection ? `\nFabric: ${savedOrder.fabric_selection.rc_code}` : '';
          
          const emailBody = `Dear ${customerName},

Thank you for your order with Laguerre. Your made-to-measure ${garmentLabel.toLowerCase()} order has been submitted to our factory.

Order Details:
Order Number: ${savedOrder.order_number}
Garment: ${garmentLabel}${fabricInfo}
Estimated Completion: ${format(new Date(savedOrder.estimated_arrival), "MMMM d, yyyy")}

We will keep you updated on the progress of your order. If you have any questions, please don't hesitate to contact us.

Best regards,
The Laguerre Team`;

          await axios.post(`${API}/admin/send-email`, {
            recipient_email: clientEmail,
            recipient_name: customerName,
            subject: `Laguerre - Order ${savedOrder.order_number} Submitted`,
            body: emailBody
          });
          toast.success(`Order ${savedOrder.order_number} saved and confirmation sent to ${clientEmail}!`);
        } catch (emailError) {
          console.error("Failed to send confirmation email:", emailError);
          toast.success(`Order ${savedOrder.order_number} saved! (Email notification failed)`);
        }
      } else {
        toast.success(`Order ${savedOrder.order_number} submitted to factory!`);
      }
    } catch (error) {
      console.error("Failed to send order:", error);
      toast.error("Failed to submit order");
    } finally {
      setSending(false);
    }
  };

  // Measurement row component
  const MeasurementRow = ({ label, fieldKey, startingSpecs, adjustments, setAdjustments }) => (
    <tr className="border-b border-white/5">
      <td className="py-2 px-3 text-sm">{label}</td>
      <td className="py-2 px-3 text-sm text-center font-mono">{startingSpecs[fieldKey] || "-"}</td>
      <td className="py-2 px-3">
        <Select value={adjustments[fieldKey]?.toString() || "0"} onValueChange={(v) => setAdjustments(prev => ({...prev, [fieldKey]: parseFloat(v)}))}>
          <SelectTrigger className="h-7 w-20 bg-background border-white/20 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ADJUSTMENT_OPTIONS.map(opt => <SelectItem key={opt.value} value={opt.value.toString()}>{opt.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </td>
      <td className="py-2 px-3 text-sm text-center font-mono text-primary font-medium">{calcFinal(startingSpecs[fieldKey], adjustments[fieldKey])}</td>
    </tr>
  );

  // Posture row component
  const PostureRow = ({ label, fieldKey, posture, setPosture }) => (
    <div className="flex items-center justify-between py-1">
      <span className="text-xs">{label}</span>
      <Select value={posture[fieldKey]?.toString() || "0"} onValueChange={(v) => setPosture(prev => ({...prev, [fieldKey]: parseFloat(v)}))}>
        <SelectTrigger className="h-7 w-20 bg-background border-white/20 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          {POSTURE_OPTIONS.map(opt => <SelectItem key={opt.value} value={opt.value.toString()}>{opt.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  // Styling select component
  const StyleSelect = ({ label, value, onChange, options }) => (
    <div className="space-y-1">
      <label className="text-xs text-muted-foreground">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-8 bg-background border-white/20 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  if (loading) {
    return <div className="min-h-screen pt-20 flex items-center justify-center"><p className="text-muted-foreground">Loading...</p></div>;
  }

  // Navigation sections
  const navSections = [
    { id: "general", label: "GENERAL INFO" },
    { id: "fabric", label: "FABRIC & LINING", isLink: true, href: "/admin/mtm-fabric" },
    { id: "measurements", label: "MEASUREMENTS", isLink: true, href: "/admin/mtm-measurements" },
    { id: "styles", label: "STYLES", isLink: true, href: "/admin/mtm-styles" },
    { id: "submit", label: "SUBMIT", isLink: true, href: "/admin/mtm-submit" },
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-12">
      {/* Keyboard Shortcuts Help Dialog */}
      <Dialog open={showShortcutsHelp} onOpenChange={setShowShortcutsHelp}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Keyboard size={20} /> Keyboard Shortcuts</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span>Save Draft</span><kbd className="px-2 py-1 bg-muted rounded text-xs">Ctrl + S</kbd></div>
            <div className="flex justify-between"><span>Print Order</span><kbd className="px-2 py-1 bg-muted rounded text-xs">Ctrl + P</kbd></div>
            <div className="flex justify-between"><span>Duplicate Order</span><kbd className="px-2 py-1 bg-muted rounded text-xs">Ctrl + D</kbd></div>
            <div className="flex justify-between"><span>Show Shortcuts</span><kbd className="px-2 py-1 bg-muted rounded text-xs">?</kbd></div>
            <div className="flex justify-between"><span>Close Dialog</span><kbd className="px-2 py-1 bg-muted rounded text-xs">Esc</kbd></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicate Order Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Copy size={20} /> Duplicate from Existing Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {orders.length === 0 ? (
              <p className="text-muted-foreground text-sm">No existing orders to duplicate from.</p>
            ) : (
              orders.slice(0, 20).map(order => (
                <div 
                  key={order.id} 
                  className="p-3 border border-white/10 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => handleDuplicateOrder(order)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-mono text-primary">{order.order_number}</p>
                      <p className="text-sm text-muted-foreground">{order.client_name}</p>
                    </div>
                    <div className="text-right text-xs text-muted-foreground">
                      <p>{GARMENT_TYPES.find(t => t.value === order.garment_type)?.label}</p>
                      <p>{order.order_date}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Print Preview Dialog */}
      <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Printer size={20} /> Print Order Sheet</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This will generate a printable factory order sheet with all measurements, styling details, and notes.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowPrintDialog(false)}>Cancel</Button>
              <Button className="flex-1" onClick={handlePrint}><Printer size={16} className="mr-2" /> Print Now</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Piping Catalog Dialog */}
      <Dialog open={showPipingCatalog} onOpenChange={setShowPipingCatalog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-cyan-400">
              RCMTM Piping Catalog
            </DialogTitle>
            <DialogDescription>
              Select piping for {activePipingGarment === 'jacket' ? 'Jacket' : activePipingGarment === 'pant' ? 'Pants' : 'Vest'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-1">
              {PIPING_CATALOG.map(piping => (
                <div 
                  key={piping.code}
                  onClick={() => {
                    const updateFn = activePipingGarment === 'jacket' ? setJacketStyling 
                      : activePipingGarment === 'pant' ? setPantStyling 
                      : setVestStyling;
                    updateFn(prev => ({
                      ...prev,
                      piping: { 
                        ...prev.piping, 
                        catalog_code: piping.code,
                        color: piping.color 
                      }
                    }));
                    setShowPipingCatalog(false);
                    toast.success(`Selected ${piping.name}`);
                  }}
                  className="p-3 border border-white/10 rounded-lg hover:border-cyan-500 hover:bg-cyan-500/10 cursor-pointer transition-all group"
                >
                  <div className="flex items-start justify-between mb-2">
                    <code className="text-xs font-mono text-cyan-400 bg-cyan-500/10 px-1 rounded">{piping.code}</code>
                    <span className="text-xs text-muted-foreground">${piping.price}</span>
                  </div>
                  <h4 className="font-medium text-sm group-hover:text-cyan-300">{piping.name}</h4>
                  <div className="flex items-center gap-2 mt-1">
                    <div 
                      className="w-4 h-4 rounded-full border border-white/20"
                      style={{ 
                        backgroundColor: piping.color === 'Black' ? '#000' 
                          : piping.color === 'Navy' ? '#1e3a5f'
                          : piping.color === 'Burgundy' ? '#722f37'
                          : piping.color === 'Charcoal' ? '#36454f'
                          : piping.color === 'White' ? '#fff'
                          : piping.color === 'Cream' ? '#fffdd0'
                          : piping.color === 'Gold' ? '#ffd700'
                          : piping.color === 'Silver' ? '#c0c0c0'
                          : piping.color === 'Red' ? '#dc143c'
                          : piping.color === 'Hunter Green' ? '#355e3b'
                          : '#666'
                      }}
                    />
                    <span className="text-xs text-muted-foreground">{piping.material}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter className="border-t border-white/10 pt-3">
            <Button variant="outline" onClick={() => setShowPipingCatalog(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header with Quick Actions */}
      <section className="py-4 border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-2">
            <Button variant="ghost" onClick={() => navigate("/admin")}>
              <ArrowLeft size={16} className="mr-2" /> Back
            </Button>
            <div className="flex items-center gap-2">
              {lastAutoSave && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock size={12} /> Saved {format(lastAutoSave, "h:mm a")}
                </span>
              )}
              <Button variant="outline" size="sm" onClick={() => setShowShortcutsHelp(true)}>
                <Keyboard size={14} className="mr-1" /> ?
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowDuplicateDialog(true)}>
                <Copy size={14} className="mr-1" /> Duplicate
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowPrintDialog(true)}>
                <Printer size={14} className="mr-1" /> Print
              </Button>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Ruler className="text-primary" size={24} />
              <div>
                <h1 className="font-serif text-2xl">Made-to-Measure Order</h1>
                <p className="text-xs text-muted-foreground">Order #{orderNumber}</p>
              </div>
            </div>
            {/* Overall Progress */}
            <div className="hidden md:flex items-center gap-3">
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Completion</p>
                <p className="text-lg font-bold text-primary">{sectionProgress.overall}%</p>
              </div>
              <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all duration-500" 
                  style={{ width: `${sectionProgress.overall}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Bar with Progress */}
      <section className="sticky top-16 z-40 bg-background/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8">
          <div className="flex items-center gap-1 overflow-x-auto py-2">
            {navSections.map((section, idx) => {
              const progress = section.id === 'general' ? sectionProgress.general 
                : section.id === 'fabric' ? sectionProgress.fabric 
                : section.id === 'measurements' ? sectionProgress.measurements 
                : 0;
              const isComplete = progress === 100;
              
              return (
                <button
                  key={section.id}
                  onClick={() => section.isLink ? navigate(section.href) : scrollToSection(section.id)}
                  className={`
                    relative flex items-center gap-2 px-4 py-2 text-xs font-medium whitespace-nowrap transition-all
                    ${section.isLink 
                      ? "bg-primary text-primary-foreground hover:bg-primary/90" 
                      : isComplete 
                        ? "bg-green-600 text-white hover:bg-green-700"
                        : "bg-slate-700 text-white hover:bg-slate-600"
                    }
                  `}
                >
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${isComplete ? 'bg-white/30' : 'bg-white/20'}`}>
                    {isComplete ? <Check size={12} /> : idx + 1}
                  </span>
                  {section.label}
                  {!section.isLink && progress > 0 && progress < 100 && (
                    <span className="ml-1 text-[10px] opacity-70">{progress}%</span>
                  )}
                </button>
              );
            })}
            
            {/* Validation Warnings */}
            {validationWarnings.length > 0 && (
              <div className="flex items-center gap-1 text-amber-400">
                <AlertTriangle size={14} />
                <span className="text-xs">{validationWarnings.length} warning{validationWarnings.length > 1 ? 's' : ''}</span>
              </div>
            )}
            
            {/* Load Order Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                fetchDraftOrders();
                setShowLoadOrderDialog(true);
              }}
              className="ml-auto border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
            >
              <FileText size={14} className="mr-1" />
              Load Order
            </Button>
            
            {/* New Order Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowNewOrderDialog(true)}
              className="border-primary/50 text-primary hover:bg-primary/10"
            >
              <RefreshCw size={14} className="mr-1" />
              New Order
            </Button>
          </div>
        </div>
      </section>

      {/* Order Form */}
      <section className="py-4">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
            {/* Main Form */}
            <div className="xl:col-span-3 space-y-4">
              {/* General Info Section */}
              <div id="general" className="bg-card border border-white/10 rounded-lg overflow-hidden scroll-mt-32">
                <div 
                  className="bg-slate-700 text-white px-3 py-2 font-medium text-sm flex items-center justify-between cursor-pointer"
                  onClick={() => toggleSection('general')}
                >
                  <div className="flex items-center gap-2">
                    <span>GENERAL INFO</span>
                    {sectionProgress.general === 100 && <Check size={14} className="text-green-400" />}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs opacity-70">{sectionProgress.general}%</span>
                    {collapsedSections.general ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                  </div>
                </div>
                <AnimatePresence>
                  {!collapsedSections.general && (
                    <motion.div 
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="p-4">
                        {/* Rush Order & Priority */}
                        <div className="flex items-center gap-4 mb-4 p-3 bg-muted/30 rounded-lg">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <Checkbox 
                              checked={rushOrder} 
                              onCheckedChange={setRushOrder}
                              className="border-amber-500 data-[state=checked]:bg-amber-500"
                            />
                            <span className="text-sm text-amber-400 font-medium">🚨 Rush Order</span>
                          </label>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">Priority:</span>
                            <Select value={priority} onValueChange={setPriority}>
                              <SelectTrigger className="w-28 h-7 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="normal">Normal</SelectItem>
                                <SelectItem value="high">High</SelectItem>
                                <SelectItem value="urgent">Urgent</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        {/* Row 1: Store, Salesperson, Date, Date Wanted */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                          <div>
                            <label className="text-xs text-muted-foreground block mb-1">Store</label>
                            <Input value={generalInfo.store} onChange={(e) => setGeneralInfo(p => ({...p, store: e.target.value}))} className="bg-background border-white/10 h-8 text-sm" />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground block mb-1">Salesperson</label>
                            <Input value={generalInfo.salesperson} onChange={(e) => setGeneralInfo(p => ({...p, salesperson: e.target.value}))} className="bg-background border-white/10 h-8 text-sm" />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground block mb-1">Date</label>
                            <Input type="date" value={generalInfo.orderDate} onChange={(e) => setGeneralInfo(p => ({...p, orderDate: e.target.value}))} className="bg-background border-white/10 h-8 text-sm" />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground block mb-1">Date Wanted</label>
                            <Input type="date" value={generalInfo.dateWanted} onChange={(e) => setGeneralInfo(p => ({...p, dateWanted: e.target.value}))} className="bg-background border-white/10 h-8 text-sm" />
                          </div>
                    </div>
                  </div>
                  
                  {/* Row 2: Order #, Garment Type, Reference */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Order #</label>
                      <Input value={orderNumber} readOnly className="bg-background/50 border-white/10 h-8 text-sm font-mono" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Garment Type</label>
                      <Select value={generalInfo.garmentType} onValueChange={(v) => setGeneralInfo(p => ({...p, garmentType: v}))}>
                        <SelectTrigger className="bg-background border-white/10 h-8 text-xs"><SelectValue placeholder="Select garment" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="2-Piece Suit">2-Piece Suit</SelectItem>
                          <SelectItem value="3-Piece Suit">3-Piece Suit</SelectItem>
                          <SelectItem value="Sports Coat">Sports Coat</SelectItem>
                          <SelectItem value="Blazer">Blazer</SelectItem>
                          <SelectItem value="Overcoat">Overcoat</SelectItem>
                          <SelectItem value="Trousers">Trousers</SelectItem>
                          <SelectItem value="Vest">Vest</SelectItem>
                          <SelectItem value="Shirt">Shirt</SelectItem>
                          <SelectItem value="Tuxedo">Tuxedo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Reference</label>
                      <Input value={generalInfo.reference} onChange={(e) => setGeneralInfo(p => ({...p, reference: e.target.value}))} className="bg-background border-white/10 h-8 text-sm" placeholder="Previous order #" />
                    </div>
                  </div>
                  
                  {/* Row 3: Customer Name */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Customer First Name *</label>
                      <Input value={generalInfo.customerFirstName} onChange={(e) => setGeneralInfo(p => ({...p, customerFirstName: e.target.value}))} className="bg-background border-white/10 h-8 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Customer Last Name *</label>
                      <Input value={generalInfo.customerLastName} onChange={(e) => setGeneralInfo(p => ({...p, customerLastName: e.target.value}))} className="bg-background border-white/10 h-8 text-sm" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs text-muted-foreground block mb-1">Email Address</label>
                      <Input type="email" value={generalInfo.email} onChange={(e) => setGeneralInfo(p => ({...p, email: e.target.value}))} className="bg-background border-white/10 h-8 text-sm" />
                    </div>
                  </div>
                  
                  {/* Row 4: Address */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Address 1</label>
                      <Input value={generalInfo.address1} onChange={(e) => setGeneralInfo(p => ({...p, address1: e.target.value}))} className="bg-background border-white/10 h-8 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Address 2</label>
                      <Input value={generalInfo.address2} onChange={(e) => setGeneralInfo(p => ({...p, address2: e.target.value}))} className="bg-background border-white/10 h-8 text-sm" />
                    </div>
                  </div>
                  
                  {/* Row 5: City, State, Zip */}
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">City</label>
                      <Input value={generalInfo.city} onChange={(e) => setGeneralInfo(p => ({...p, city: e.target.value}))} className="bg-background border-white/10 h-8 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">State</label>
                      <Input value={generalInfo.state} onChange={(e) => setGeneralInfo(p => ({...p, state: e.target.value}))} className="bg-background border-white/10 h-8 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Zip</label>
                      <Input value={generalInfo.zip} onChange={(e) => setGeneralInfo(p => ({...p, zip: e.target.value}))} className="bg-background border-white/10 h-8 text-sm" />
                    </div>
                  </div>
                  
                  {/* Row 6: Phone Numbers */}
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Phone (Day)</label>
                      <Input value={generalInfo.phoneDay} onChange={(e) => setGeneralInfo(p => ({...p, phoneDay: e.target.value}))} className="bg-background border-white/10 h-8 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Phone (Eve)</label>
                      <Input value={generalInfo.phoneEve} onChange={(e) => setGeneralInfo(p => ({...p, phoneEve: e.target.value}))} className="bg-background border-white/10 h-8 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Cell Phone</label>
                      <Input value={generalInfo.cellPhone} onChange={(e) => setGeneralInfo(p => ({...p, cellPhone: e.target.value}))} className="bg-background border-white/10 h-8 text-sm" />
                    </div>
                  </div>
                  
                  {/* Create Profile Button - Prominent */}
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <div className="flex items-center gap-3">
                      <Button 
                        onClick={handleCreateCRMProfile}
                        disabled={creatingCRMProfile || (!generalInfo.customerFirstName && !generalInfo.customerLastName)}
                        className="bg-primary hover:bg-primary/90 h-10 px-6"
                      >
                        {creatingCRMProfile ? (
                          <>Creating...</>
                        ) : (
                          <><UserPlus size={16} className="mr-2" /> Create Profile</>
                        )}
                      </Button>
                      {selectedClient && (
                        <span className="text-sm text-green-400 flex items-center gap-1">
                          <Check size={14} /> Linked to: {selectedClient.name}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">Creates a CRM profile with all the customer info above</p>
                  </div>
                  
                  {/* Load CRM Client Info */}
                  <div className="border-t border-white/10 pt-3 mt-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <label className="text-xs text-muted-foreground block mb-1">Load CRM Info</label>
                        <Popover open={clientOpen} onOpenChange={setClientOpen}>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-between bg-background border-white/10 h-8 text-xs">
                              {selectedClient ? selectedClient.name : "Select client to load info..."} <ChevronDown className="ml-1 h-3 w-3 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56 p-0" align="start">
                            <Command>
                              <CommandInput placeholder="Search..." />
                              <CommandList>
                                <CommandEmpty>No clients found</CommandEmpty>
                                <CommandGroup>
                                  {clients.map((c) => (
                                    <CommandItem key={c.id} value={c.name} onSelect={() => { 
                                      setClientOpen(false);
                                      
                                      // Check if client has any measurements
                                      const hasMeasurements = c.last_jacket_measurements || c.last_pant_measurements || c.last_vest_measurements;
                                      
                                      if (hasMeasurements) {
                                        // Show measurement selection inline
                                        setPendingClient(c);
                                        setSelectedMeasurements({
                                          jacket: !!c.last_jacket_measurements,
                                          trouser: !!c.last_pant_measurements,
                                          vest: !!c.last_vest_measurements
                                        });
                                      } else {
                                        // No measurements, just load info
                                        setSelectedClient(c);
                                        setPendingClient(null);
                                        setGeneralInfo(p => ({
                                          ...p,
                                          customerFirstName: c.name?.split(' ')[0] || '',
                                          customerLastName: c.name?.split(' ').slice(1).join(' ') || '',
                                          email: c.email || '',
                                          cellPhone: c.phone || '',
                                          phoneDay: c.phone_day || '',
                                          phoneEve: c.phone_eve || '',
                                          address1: c.address1 || c.address?.split(',')[0] || '',
                                          address2: c.address2 || '',
                                          city: c.city || '',
                                          state: c.state || '',
                                          zip: c.zip || '',
                                        }));
                                        toast.success(`Loaded info for ${c.name}`);
                                      }
                                    }}>
                                      <Check className={`mr-2 h-3 w-3 ${selectedClient?.id === c.id ? "opacity-100" : "opacity-0"}`} />
                                      <div className="flex-1">
                                        <span>{c.name}</span>
                                        {(c.last_jacket_measurements || c.last_pant_measurements || c.last_vest_measurements) && (
                                          <span className="text-[10px] text-muted-foreground ml-1">
                                            ({[c.last_jacket_measurements && 'J', c.last_pant_measurements && 'T', c.last_vest_measurements && 'V'].filter(Boolean).join(',')})
                                          </span>
                                        )}
                                      </div>
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                  </div>
                  
                  {/* Inline Measurement Selection - Shows when client with measurements is selected */}
                  {pendingClient && (
                    <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                      <h4 className="text-sm font-medium text-blue-400 mb-1">Load Measurements for {pendingClient.name}</h4>
                      <p className="text-xs text-muted-foreground mb-4">Select which measurements and styling to load from previous orders</p>
                      
                      <div className="space-y-2 mb-4">
                        {pendingClient?.last_jacket_measurements && (
                          <label className="flex items-center gap-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg cursor-pointer hover:bg-blue-500/20 transition-colors">
                            <Checkbox 
                              checked={selectedMeasurements.jacket}
                              onCheckedChange={(checked) => setSelectedMeasurements(p => ({...p, jacket: checked}))}
                              className="border-blue-400 data-[state=checked]:bg-blue-500"
                            />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-blue-400">Jacket</p>
                              <p className="text-xs text-muted-foreground">
                                Size: {pendingClient.last_jacket_measurements.tryon_size || 'N/A'} • Measurements & Styling
                              </p>
                            </div>
                          </label>
                        )}
                        {pendingClient?.last_pant_measurements && (
                          <label className="flex items-center gap-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg cursor-pointer hover:bg-blue-500/20 transition-colors">
                            <Checkbox 
                              checked={selectedMeasurements.trouser}
                              onCheckedChange={(checked) => setSelectedMeasurements(p => ({...p, trouser: checked}))}
                              className="border-blue-400 data-[state=checked]:bg-blue-500"
                            />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-blue-400">Trousers</p>
                              <p className="text-xs text-muted-foreground">
                                Size: {pendingClient.last_pant_measurements.tryon_size || 'N/A'} • Measurements & Styling
                              </p>
                            </div>
                          </label>
                        )}
                        {pendingClient?.last_vest_measurements && (
                          <label className="flex items-center gap-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg cursor-pointer hover:bg-blue-500/20 transition-colors">
                            <Checkbox 
                              checked={selectedMeasurements.vest}
                              onCheckedChange={(checked) => setSelectedMeasurements(p => ({...p, vest: checked}))}
                              className="border-blue-400 data-[state=checked]:bg-blue-500"
                            />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-blue-400">Vest</p>
                              <p className="text-xs text-muted-foreground">
                                Size: {pendingClient.last_vest_measurements.tryon_size || 'N/A'} • Measurements & Styling
                              </p>
                            </div>
                          </label>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setPendingClient(null)}>
                          Cancel
                        </Button>
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => {
                          const c = pendingClient;
                          setSelectedClient(c);
                          
                          // Load client info
                          setGeneralInfo(p => ({
                            ...p,
                            customerFirstName: c.name?.split(' ')[0] || '',
                            customerLastName: c.name?.split(' ').slice(1).join(' ') || '',
                            email: c.email || '',
                            cellPhone: c.phone || '',
                            phoneDay: c.phone_day || '',
                            phoneEve: c.phone_eve || '',
                            address1: c.address1 || c.address?.split(',')[0] || '',
                            address2: c.address2 || '',
                            city: c.city || '',
                            state: c.state || '',
                            zip: c.zip || '',
                          }));
                          
                          let loadedItems = ['info'];
                          
                          // Load selected measurements
                          if (selectedMeasurements.jacket && c.last_jacket_measurements) {
                            setIncludeJacket(true);
                            if (c.last_jacket_measurements.tryon_size) setJacketModelSize(c.last_jacket_measurements.tryon_size);
                            if (c.last_jacket_measurements.starting_specs) setJacketStartingSpecs(c.last_jacket_measurements.starting_specs);
                            if (c.last_jacket_measurements.adjustments) setJacketAdjustments(c.last_jacket_measurements.adjustments);
                            if (c.last_jacket_measurements.posture) setJacketPosture(c.last_jacket_measurements.posture);
                            if (c.last_jacket_measurements.styling) setJacketStyling(prev => ({ ...prev, ...c.last_jacket_measurements.styling }));
                            if (c.last_jacket_measurements.remarks) setJacketRemarks(c.last_jacket_measurements.remarks);
                            loadedItems.push('jacket');
                          }
                          
                          if (selectedMeasurements.trouser && c.last_pant_measurements) {
                            setIncludeTrouser(true);
                            if (c.last_pant_measurements.tryon_size) setPantModelSize(c.last_pant_measurements.tryon_size);
                            if (c.last_pant_measurements.starting_specs) setPantStartingSpecs(c.last_pant_measurements.starting_specs);
                            if (c.last_pant_measurements.adjustments) setPantAdjustments(c.last_pant_measurements.adjustments);
                            if (c.last_pant_measurements.posture) setPantPosture(c.last_pant_measurements.posture);
                            if (c.last_pant_measurements.styling) setPantStyling(prev => ({ ...prev, ...c.last_pant_measurements.styling }));
                            if (c.last_pant_measurements.remarks) setPantRemarks(c.last_pant_measurements.remarks);
                            loadedItems.push('trousers');
                          }
                          
                          if (selectedMeasurements.vest && c.last_vest_measurements) {
                            setIncludeVest(true);
                            if (c.last_vest_measurements.tryon_size) setVestModelSize(c.last_vest_measurements.tryon_size);
                            if (c.last_vest_measurements.starting_specs) setVestStartingSpecs(c.last_vest_measurements.starting_specs);
                            if (c.last_vest_measurements.adjustments) setVestAdjustments(c.last_vest_measurements.adjustments);
                            if (c.last_vest_measurements.posture) setVestPosture(c.last_vest_measurements.posture);
                            if (c.last_vest_measurements.styling) setVestStyling(prev => ({ ...prev, ...c.last_vest_measurements.styling }));
                            if (c.last_vest_measurements.remarks) setVestRemarks(c.last_vest_measurements.remarks);
                            loadedItems.push('vest');
                          }
                          
                          toast.success(`Loaded ${c.name}: ${loadedItems.join(', ')}`);
                          setPendingClient(null);
                        }}>
                          Load Selected
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Previous Orders Section - Shows when a client is selected */}
                  {selectedClient && clientOrders.length > 0 && (
                    <div className="mt-4 p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-purple-400 flex items-center gap-2">
                          <Clock size={14} />
                          Previous Orders ({clientOrders.length})
                        </h4>
                        <span className="text-xs text-muted-foreground">Click to copy from order</span>
                      </div>
                      
                      <div className="space-y-2 max-h-[200px] overflow-y-auto">
                        {clientOrders.slice(0, 5).map((order) => (
                          <div 
                            key={order.id}
                            className="flex items-center justify-between p-3 bg-purple-500/5 border border-purple-500/20 rounded-lg hover:bg-purple-500/15 cursor-pointer transition-colors"
                            onClick={() => {
                              setSelectedOrderToCopy(order);
                              setShowCopyOrderDialog(true);
                            }}
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-sm text-purple-400">{order.order_number}</span>
                                <span className={`text-[10px] px-2 py-0.5 rounded ${
                                  order.status === 'complete' ? 'bg-green-500/20 text-green-400' :
                                  order.status === 'shipped' ? 'bg-blue-500/20 text-blue-400' :
                                  order.status === 'in_production' ? 'bg-yellow-500/20 text-yellow-400' :
                                  'bg-gray-500/20 text-gray-400'
                                }`}>
                                  {order.status}
                                </span>
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">
                                {order.garment_type} • {order.fabric_selection || 'No fabric'} • {format(new Date(order.created_at), "MMM d, yyyy")}
                              </div>
                            </div>
                            <Copy size={14} className="text-purple-400 opacity-50 group-hover:opacity-100" />
                          </div>
                        ))}
                      </div>
                      
                      {clientOrders.length > 5 && (
                        <p className="text-xs text-muted-foreground text-center mt-2">
                          +{clientOrders.length - 5} more orders
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Loading client orders indicator */}
                  {selectedClient && loadingClientOrders && (
                    <div className="mt-4 p-4 bg-muted/30 border border-white/10 rounded-lg text-center">
                      <p className="text-sm text-muted-foreground">Loading previous orders...</p>
                    </div>
                  )}
                  
                  {/* Save & Continue Button */}
                  <div className="mt-4 pt-4 border-t border-white/10 flex justify-end">
                    <Button onClick={() => navigate("/admin/mtm-fabric")} className="bg-primary hover:bg-primary/90">
                      Save & Continue to Fabric <ChevronRight size={16} className="ml-2" />
                    </Button>
                  </div>
                  
                  {/* Factory Notes Section */}
                  <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <label className="text-xs text-amber-400 font-medium flex items-center gap-1 mb-2">
                      <MessageSquare size={12} /> Factory Notes (Internal Only)
                    </label>
                    <Textarea 
                      value={factoryNotes} 
                      onChange={(e) => setFactoryNotes(e.target.value)}
                      placeholder="Special instructions for the factory, production notes, etc..."
                      className="bg-background/50 border-amber-500/30 text-sm min-h-[60px] resize-none"
                    />
                  </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* ============ NEW: MEASUREMENT METHOD & FIT PREFERENCES SECTION ============ */}
              {(includeJacket || includeTrouser || includeVest || includeShirt) && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                  
                  {/* Measurement Method & Fitter */}
                  <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
                    <div className="bg-indigo-600 text-white px-3 py-2 font-medium text-sm flex items-center gap-2">
                      <Ruler size={14} /> MEASUREMENT INFO
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <label className="text-xs text-muted-foreground block mb-1">Measurement Method *</label>
                          <Select value={measurementMethod} onValueChange={setMeasurementMethod}>
                            <SelectTrigger className="bg-background border-white/10 h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {MEASUREMENT_METHODS.map(m => (
                                <SelectItem key={m.value} value={m.value}>
                                  <div className="flex flex-col">
                                    <span>{m.label}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-[10px] text-muted-foreground mt-1">
                            {MEASUREMENT_METHODS.find(m => m.value === measurementMethod)?.description}
                          </p>
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground block mb-1">Fitter / Measured By</label>
                          <Input 
                            value={fitter} 
                            onChange={(e) => setFitter(e.target.value)}
                            placeholder={generalInfo.salesperson || "Fitter name"}
                            className="bg-background border-white/10 h-9"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground block mb-1">Profile Name (Optional)</label>
                          <Input 
                            value={measurementProfileName} 
                            onChange={(e) => setMeasurementProfileName(e.target.value)}
                            placeholder="e.g., Wedding 2025"
                            className="bg-background border-white/10 h-9"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-3">
              {/* Navigation Help */}
              <div className="bg-card border border-white/10 p-4 rounded-lg">
                <h3 className="font-medium text-sm mb-3">Order Steps</h3>
                <div className="space-y-2 text-xs">
                  <div className="flex items-center gap-2 p-2 bg-primary/10 rounded">
                    <span className="w-5 h-5 rounded-full bg-primary flex items-center justify-center text-white text-[10px]">1</span>
                    <span className="font-medium">General Info</span>
                    <span className="text-muted-foreground ml-auto">Current</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 hover:bg-muted/50 rounded cursor-pointer" onClick={() => navigate("/admin/mtm-fabric")}>
                    <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-[10px]">2</span>
                    <span>Fabric & Lining</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 hover:bg-muted/50 rounded cursor-pointer" onClick={() => navigate("/admin/mtm-measurements")}>
                    <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-[10px]">3</span>
                    <span>Measurements</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 hover:bg-muted/50 rounded cursor-pointer" onClick={() => navigate("/admin/mtm-styles")}>
                    <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-[10px]">4</span>
                    <span>Styles</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 hover:bg-muted/50 rounded cursor-pointer" onClick={() => navigate("/admin/mtm-submit")}>
                    <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-[10px]">5</span>
                    <span>Review & Submit</span>
                  </div>
                </div>
                <Button 
                  className="w-full mt-4"
                  onClick={() => navigate("/admin/mtm-fabric")}
                >
                  Continue to Fabric →
                </Button>
              </div>

              {/* Validation Warnings */}
              {validationWarnings.length > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded-lg">
                  <h3 className="font-medium text-xs mb-2 flex items-center gap-2 text-amber-400">
                    <AlertTriangle size={14} /> Warnings
                  </h3>
                  <div className="space-y-1">
                    {validationWarnings.map((warning, idx) => (
                      <p key={idx} className="text-[10px] text-amber-300">{warning.message}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* New Client Dialog */}
      <Dialog open={newClientDialog} onOpenChange={setNewClientDialog}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader><DialogTitle>New Client</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            <Input value={newClient.name} onChange={(e) => setNewClient(p => ({...p, name: e.target.value}))} placeholder="Name *" className="bg-background border-white/10" />
            <Input value={newClient.email} onChange={(e) => setNewClient(p => ({...p, email: e.target.value}))} placeholder="Email" className="bg-background border-white/10" />
            <Input value={newClient.phone} onChange={(e) => setNewClient(p => ({...p, phone: e.target.value}))} placeholder="Phone" className="bg-background border-white/10" />
            <Button onClick={handleCreateClient} className="w-full"><Plus size={14} className="mr-1" /> Create</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fabric Selection Dialog */}
      <Dialog open={showFabricDialog} onOpenChange={setShowFabricDialog}>
        <DialogContent className="bg-card border-white/10 max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Select Fabric from RCMTM Catalog (RC + MX)</span>
              {fabricSelection && (
                <Button variant="ghost" size="sm" onClick={() => setFabricSelection(null)}>
                  <X size={14} className="mr-1" /> Clear
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Category Tabs */}
            <div className="flex gap-2 border-b border-white/10 pb-2 flex-wrap">
              {[
                { key: "suits", label: "RC Suits", count: FABRIC_DATA.suits?.length || 0 },
                { key: "mx", label: "MX Fabrics", count: MX_FABRIC_DATA?.length || 0 },
                { key: "overcoats", label: "Overcoats", count: FABRIC_DATA.overcoats?.length || 0 },
                { key: "shirts", label: "Shirts", count: FABRIC_DATA.shirts?.length || 0 },
              ].map(cat => (
                <Button
                  key={cat.key}
                  variant={fabricCategory === cat.key ? "default" : "ghost"}
                  size="sm"
                  onClick={() => { setFabricCategory(cat.key); setFabricSearch(""); }}
                  className={`text-xs ${cat.key === "mx" ? "bg-purple-500/20 hover:bg-purple-500/30 text-purple-400" : ""}`}
                >
                  {cat.label} ({cat.count})
                </Button>
              ))}
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
              <Input
                placeholder="Search by code, color, pattern, composition..."
                value={fabricSearch}
                onChange={(e) => setFabricSearch(e.target.value)}
                className="pl-10 bg-background border-white/10"
              />
            </div>

            {/* Results */}
            <div className="max-h-[400px] overflow-y-auto border border-white/10 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 text-xs">Book</th>
                    <th className="text-left p-2 text-xs">RC Code</th>
                    <th className="text-left p-2 text-xs">Composition</th>
                    <th className="text-left p-2 text-xs">Pattern</th>
                    <th className="text-left p-2 text-xs">Color</th>
                    <th className="text-right p-2 text-xs">Price</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFabrics.map((fabric, idx) => (
                    <tr 
                      key={`${fabric.rc_code}-${idx}`}
                      className={`border-b border-white/5 hover:bg-muted/30 cursor-pointer ${
                        fabricSelection?.rc_code === fabric.rc_code ? "bg-primary/20" : 
                        fabric.source === "MX" ? "bg-purple-500/5" : ""
                      }`}
                      onClick={() => {
                        setFabricSelection(fabric);
                        setShowFabricDialog(false);
                        toast.success(`Selected: ${fabric.rc_code}`);
                      }}
                    >
                      <td className="p-2 text-xs text-muted-foreground">
                        {fabric.book}
                        {fabric.source === "MX" && <span className="ml-1 px-1 py-0.5 bg-purple-500/20 text-purple-400 text-[10px] rounded">MX</span>}
                      </td>
                      <td className={`p-2 font-mono text-xs ${fabric.source === "MX" ? "text-purple-400" : ""}`}>{fabric.rc_code}</td>
                      <td className="p-2 text-xs">{fabric.composition || "-"}</td>
                      <td className="p-2 text-xs">{fabric.pattern || "-"}</td>
                      <td className="p-2 text-xs">{fabric.color || "-"}</td>
                      <td className="p-2 text-xs text-right text-emerald-400">{fabric.price ? `$${fabric.price}` : "-"}</td>
                      <td className="p-2">
                        {fabricSelection?.rc_code === fabric.rc_code && (
                          <Check size={14} className="text-primary" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredFabrics.length === 0 && (
                <div className="p-8 text-center text-muted-foreground">
                  No fabrics found matching your search
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Showing {filteredFabrics.length} of {fabricCategory === "mx" ? MX_FABRIC_DATA.length : (FABRIC_DATA[fabricCategory] || []).length} fabrics
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lining Selection Dialog */}
      <Dialog open={showLiningDialog} onOpenChange={setShowLiningDialog}>
        <DialogContent className="bg-card border-white/10 max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Select Lining from RCMTM Catalog</span>
              {liningSelection && (
                <Button variant="ghost" size="sm" onClick={() => setLiningSelection(null)}>
                  <X size={14} className="mr-1" /> Clear
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
              <Input
                placeholder="Search by lining code..."
                value={liningSearch}
                onChange={(e) => setLiningSearch(e.target.value)}
                className="pl-10 bg-background border-white/10"
              />
            </div>

            {/* Results */}
            <div className="max-h-[400px] overflow-y-auto border border-white/10 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 text-xs">Book</th>
                    <th className="text-left p-2 text-xs">RC Code</th>
                    <th className="text-left p-2 text-xs">New</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLinings.map((lining, idx) => (
                    <tr 
                      key={`${lining.rc_code}-${idx}`}
                      className={`border-b border-white/5 hover:bg-muted/30 cursor-pointer ${
                        liningSelection?.rc_code === lining.rc_code ? "bg-primary/20" : ""
                      }`}
                      onClick={() => {
                        setLiningSelection(lining);
                        setShowLiningDialog(false);
                        toast.success(`Selected: ${lining.rc_code}`);
                      }}
                    >
                      <td className="p-2 text-xs text-muted-foreground">{lining.book}</td>
                      <td className="p-2 font-mono text-xs">{lining.rc_code}</td>
                      <td className="p-2 text-xs">
                        {lining.is_new && (
                          <span className="bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded text-[10px]">NEW</span>
                        )}
                      </td>
                      <td className="p-2">
                        {liningSelection?.rc_code === lining.rc_code && (
                          <Check size={14} className="text-primary" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredLinings.length === 0 && (
                <div className="p-8 text-center text-muted-foreground">
                  No linings found matching your search
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Showing {filteredLinings.length} of {(FABRIC_DATA.linings || []).length} linings
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Copy Order Dialog */}
      <Dialog open={showCopyOrderDialog} onOpenChange={setShowCopyOrderDialog}>
        <DialogContent className="bg-card border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle>Copy from Previous Order</DialogTitle>
            <DialogDescription>
              Select what to copy from order <span className="font-mono text-primary">{selectedOrderToCopy?.order_number}</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            <label className="flex items-center gap-3 p-3 bg-muted/30 border border-white/10 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
              <Checkbox 
                checked={copyOptions.measurements}
                onCheckedChange={(checked) => setCopyOptions(p => ({...p, measurements: checked}))}
              />
              <div className="flex-1">
                <p className="text-sm font-medium">Measurements</p>
                <p className="text-xs text-muted-foreground">Jacket, Trouser, Vest sizes and adjustments</p>
              </div>
            </label>
            
            <label className="flex items-center gap-3 p-3 bg-muted/30 border border-white/10 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
              <Checkbox 
                checked={copyOptions.fabric}
                onCheckedChange={(checked) => setCopyOptions(p => ({...p, fabric: checked}))}
              />
              <div className="flex-1">
                <p className="text-sm font-medium">Fabric & Lining</p>
                <p className="text-xs text-muted-foreground">Fabric selection, lining type, piping</p>
              </div>
            </label>
            
            <label className="flex items-center gap-3 p-3 bg-muted/30 border border-white/10 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
              <Checkbox 
                checked={copyOptions.buttons}
                onCheckedChange={(checked) => setCopyOptions(p => ({...p, buttons: checked}))}
              />
              <div className="flex-1">
                <p className="text-sm font-medium">Buttons</p>
                <p className="text-xs text-muted-foreground">Button selections for all garments</p>
              </div>
            </label>
            
            <label className="flex items-center gap-3 p-3 bg-muted/30 border border-white/10 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
              <Checkbox 
                checked={copyOptions.styles}
                onCheckedChange={(checked) => setCopyOptions(p => ({...p, styles: checked}))}
              />
              <div className="flex-1">
                <p className="text-sm font-medium">Styles</p>
                <p className="text-xs text-muted-foreground">Lapel, vent, pockets, all styling options</p>
              </div>
            </label>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCopyOrderDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => copyFromOrder(selectedOrderToCopy)} className="bg-purple-600 hover:bg-purple-700">
              <Copy size={14} className="mr-2" />
              Copy Selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Order Confirmation Dialog */}
      <AlertDialog open={showNewOrderDialog} onOpenChange={setShowNewOrderDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <RefreshCw size={20} className="text-primary" />
              Start New Order
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will clear all current order information and start fresh with the next order number.
              {generalInfo.customerFirstName && (
                <span className="block mt-2 text-amber-400">
                  Current order has customer data for: {generalInfo.customerFirstName} {generalInfo.customerLastName}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-4 space-y-3">
            <p className="text-sm text-muted-foreground">Choose an option:</p>
            
            <Button
              variant="outline"
              className="w-full justify-start h-auto p-4 border-amber-500/30 hover:bg-amber-500/10"
              onClick={() => startNewOrder(true)}
              disabled={savingBeforeNew}
            >
              <div className="flex items-start gap-3">
                <Save size={18} className="text-amber-400 mt-0.5" />
                <div className="text-left">
                  <p className="font-medium">Save Draft & Start New</p>
                  <p className="text-xs text-muted-foreground">Save current order as a draft backup, then clear for new order</p>
                </div>
              </div>
            </Button>
            
            <Button
              variant="outline"
              className="w-full justify-start h-auto p-4 border-red-500/30 hover:bg-red-500/10"
              onClick={() => startNewOrder(false)}
            >
              <div className="flex items-start gap-3">
                <X size={18} className="text-red-400 mt-0.5" />
                <div className="text-left">
                  <p className="font-medium">Clear & Start New</p>
                  <p className="text-xs text-muted-foreground">Discard current order and start fresh</p>
                </div>
              </div>
            </Button>
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Load Order Dialog */}
      <Dialog open={showLoadOrderDialog} onOpenChange={setShowLoadOrderDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={20} className="text-amber-400" />
              Load Saved Order
            </DialogTitle>
            <DialogDescription>
              Select a pending or draft order to continue working on it
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {loadingDrafts ? (
              <div className="text-center py-8">
                <RefreshCw size={24} className="animate-spin mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Loading orders...</p>
              </div>
            ) : draftOrders.length === 0 ? (
              <div className="text-center py-8">
                <FileText size={32} className="mx-auto text-muted-foreground mb-2" />
                <p className="text-muted-foreground">No pending or draft orders found</p>
                <p className="text-xs text-muted-foreground mt-1">Create a new order to get started</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {draftOrders.map((order) => (
                  <div
                    key={order.id}
                    onClick={() => loadSavedOrder(order)}
                    className="p-4 border border-white/10 rounded-lg hover:border-amber-500/50 hover:bg-amber-500/5 cursor-pointer transition-all group"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-amber-400 font-medium">{order.order_number}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded ${
                            order.status === 'draft' ? 'bg-gray-500/20 text-gray-400' :
                            order.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-blue-500/20 text-blue-400'
                          }`}>
                            {order.status}
                          </span>
                        </div>
                        <p className="text-sm font-medium">{order.client_name || "No client name"}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span>{order.garment_type || "No garment type"}</span>
                          <span>•</span>
                          <span>{order.fabric_selection || "No fabric"}</span>
                          <span>•</span>
                          <span>{format(new Date(order.created_at), "MMM d, yyyy")}</span>
                        </div>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button size="sm" variant="ghost" className="text-amber-400">
                          Load <ChevronRight size={14} className="ml-1" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLoadOrderDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMTMOrderPage;
